import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle, CalendarDays, ChevronDown, ChevronRight, FilePen, Link2, Loader2, RefreshCw, Search, Unlink,
} from 'lucide-react';

import { getPanelOrders, poDetail, type OrdersPanelDto, type OrdersPanelRowDto, type PoDetailResult } from '@/api/client';
import { SECTION_HEADER, appointmentTimeLabel, hhmm, isDayOnly, staleness } from '@/lib/utils';
import {
    ordersMatchesFocus, focusBasisLabel, focusScopeLabel, focusBlockedReason, type RailFocus,
} from '@/lib/railFocus';
import { onBoardAny, boardScopeNote } from '@/lib/boardScope';
import { useRailSlotClose } from '@/lib/railSlot';
import { setWmsOrderDrag, wmsOrderKeyOf, type WmsOrderDragKey } from '@/lib/dnd';
import { loadedOrderMatches } from '@/lib/orderLocalSearch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

/**
 * ============================================================================
 * WMS ORDERS — WHAT IS DUE IN THE RANGE ON SCREEN, AND WHETHER IT IS BOOKED
 * ============================================================================
 *
 * > Pete, 2026-09-09: *"The new Panel should be called WMS Orders"* … *"have the
 * > same icon as the Scanned Paperwork icon but lets make it a color"* … then,
 * > minutes later: *"Actually, lets keep it black and white like it is."*
 *
 * > *"It will obvioulsy be poplulated automatically instead of initiating a
 * > search"* … *"A refresh button on the far right to refresh the default
 * > search. A search bar below the panel for specific searches across all
 * > dates."* … *"It should also identify if the item is already on an
 * > appointment and show that somehow"*
 *
 * ---- ⭐ THE SCOPE IS THE CALENDAR'S, NOT THIS PANEL'S ---------------------
 *
 * §1Z.1: *"A search's default scope should be what is on screen."* The bounds
 * arrive as props from `Calendar`, which already computes day/week/month —
 * including the six-week grid the month view actually draws. ⛔ This file does
 * NOT re-derive them: that arithmetic is duplicated once already and pinned by
 * `tools/search-scope-guard.cjs`, and a third copy is how a search starts
 * returning less than the page is showing.
 *
 * ---- ⛔⛔ AND §1Z.2 IS PAID FOR, IN THREE PLACES --------------------------
 *
 * *"Nothing found is what a scheduler mistakes for 'not booked' before turning a
 * driver away from a gate."* So:
 *   1. the scope sentence renders on EVERY result — full, short or empty,
 *      because a short list is the same lie in miniature;
 *   2. the search box below is the next rung, offered in place;
 *   3. ⭐ the server counts matches OUTSIDE the scope and the panel offers them:
 *      "3 on other dates — search every date".
 * ============================================================================
 */

/** ⚠️ Camelot / Deposco are the two live sources; OTHER is Pete's future-proofing. */
const KNOWN_SOURCES = ['Camelot', 'Deposco'] as const;
const ALL_SOURCE_KEYS = [...KNOWN_SOURCES, 'OTHER'] as const;
type SourceKey = typeof ALL_SOURCE_KEYS[number];

const POLL_MS = 60_000;

/**
 * ---- ⭐⭐ THE STATUS BOXES, IN PLAIN WORDS (#359) -------------------------
 *
 * > Pete, 2026-09-09: *"lets put the mapping in laman terms in an (i) on the WMS
 * > Panel so people know what its pulling as default"*
 *
 * ⛔ THE LABELS ARE PLAIN ENGLISH; THE MEMBERSHIP IS NOT HERE. Which statuses
 * count as "Finished" lives in `dock.WmsOrderStatusGroup` and arrives with the
 * response, because `Posted` and `Received` are judgement calls both vendors
 * have been asked about — and the answer has to be an UPDATE, not a deploy.
 *
 * ⛔⛔ THERE IS NO ROW FOR "UNRECOGNISED" AND THERE MUST NOT BE. A status nobody
 * has classified is always shown. A checkbox would invite hiding precisely the
 * orders we understand least, which is how a truck goes missing off the board.
 */
const STATUS_BOXES = [
    { key: 'OPEN',      label: 'Open',      hint: 'Still expected - a truck may still be coming' },
    { key: 'DONE',      label: 'Finished',  hint: 'The WMS considers it shipped or received' },
    { key: 'CANCELLED', label: 'Cancelled', hint: 'Voided or cancelled in the WMS' },
] as const;
type StatusGroupKey = typeof STATUS_BOXES[number]['key'];

function ymd(iso: string | null): string {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime())
        ? '—'
        : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

/* ⛔⤴ ~~`shortDay`~~ — its only caller, the "Booked {range}" box, went with B6
   (2026-09-28). Its warning still holds for anyone rendering a YYYY-MM-DD: `new
   Date('2026-09-08')` is UTC midnight, so west of Greenwich it reads Sep 7. */

/**
 * ---- ⭐⭐⭐ "on DD23 at 9:00 AM" — WHERE TO LOOK, NOT JUST WHICH ID ----------
 *
 * > Pete, 2026-09-27: *"For the On A000000xxxx of the order card, if you could
 * > add after the A000000xxx on DD23 at 9:00am (dock door and appointment time)
 * > that would be great so they can find it visually too."*
 *
 * ⭐ THE POINT IS THE LAST FIVE WORDS. `A00000406` is a key — correct, unique and
 * unfindable on a board of coloured chips laid out by DOOR and TIME. The door and
 * the clock are the two coordinates the grid is actually drawn in.
 *   ** AN IDENTIFIER LOCATES A ROW IN A TABLE. IT DOES NOT LOCATE A TRUCK ON A
 *      WALL. **
 *
 * ---- ⛔⛔ EVERY PART IS OPTIONAL, AND NONE OF THEM IS FAKED -----------------
 *
 * ⚠️ NO DOOR: a booking can be unassigned, and the query deliberately yields NULL
 * rather than a code when the door belongs to another facility. The sentence drops
 * `on DD23` and keeps the time.
 *
 * ⛔⛔ DAY-ONLY: `appointmentTimeLabel` DECIDES, NOT THIS FUNCTION. A day-only
 * booking's `scheduledStartUtc` is the facility's OPENING time — stored wide so
 * capacity queries can see it, and never a time anybody agreed to. Printing it
 * here would put `at 5:00 AM` on a card a scheduler then reads out to a driver.
 *   ** A STORED WINDOW IS NOT A PROMISE SOMEBODY MADE. **
 * ⭐ So this asks the shared helper and gets back `All day`, which is lower-cased
 * into the sentence — the same string the grid and the dialogs show.
 *
 * ⚠️ AND IT RETURNS `''` RATHER THAN A PLACEHOLDER when it knows neither. An
 * empty suffix leaves `On A00000406` exactly as it read yesterday; a `— unknown`
 * would add a word that tells the reader nothing they did not already see.
 */
/**
 * ---- ⭐⭐⭐ D8 — DATE FIRST, THEN THE EARLIEST TRUCK (Pete, 2026-09-27) -------
 *
 * > *"you sorting by Date in the upper right hand corner is better, lets to it
 * > that way, then by earliest time of day on the Appointment. So Sept 30th, then
 * > 8:00 am appointments, 9:00 am appts, etc."*
 *
 * ⛔⛔ THIS SORTS THE PAGE, IT DOES NOT CHOOSE THE PAGE. Pete's answer, asked
 * directly: *"Re-sort on screen, leave the SQL alone."* The repository still
 * orders matched-before-unmatched, and `MAX_ORDERS` still truncates in THAT
 * order — so the 200-row cap keeps eating the unmatched tail and can never drop
 * a matched order, which is the rule he set when the cap was found.
 *   ** THE ORDER YOU READ IN AND THE ORDER YOU CUT AT ARE TWO DIFFERENT
 *      DECISIONS. SORTING THE PAGE BY DATE WOULD HAVE CUT IT BY DATE TOO. **
 * ⚠️ So the screen and the query DISAGREE ON PURPOSE, and this comment is the
 * only place that says why. Do not "fix" one to match the other.
 *
 * ---- ⚠️ FOUR TIERS INSIDE A DATE, AND THE MIDDLE TWO ARE THE SUBTLE ONES ----
 *
 * 1. the date shown upper-right (`effectiveDate`), ascending — Sept 30, then Oct 1
 * 2. within a date: TIMED bookings by their earliest start — 8:00, then 9:00
 * 3. then DAY-ONLY bookings. ⛔ A day-only appointment's `scheduledStartUtc` is
 *    the facility's OPENING time — stored wide so capacity queries can see it,
 *    never a time anybody agreed to. Sorting on it naively files a timeless truck
 *    above the 8:00 one at "5:00 AM", which is a promise nobody made appearing as
 *    a position in a list.
 *      ** A STORED WINDOW IS NOT A PROMISE SOMEBODY MADE — INCLUDING WHEN THE
 *         ONLY THING IT DECIDES IS A ROW'S POSITION. **
 * 4. then UNBOOKED orders, which have no truck to be early or late.
 *
 * ⚠️ `orderNumber` BREAKS EVERY TIE, so the list does not reshuffle between two
 * renders of identical data. An unstable sort on equal keys reads as the panel
 * twitching.
 *
 * ⛔ A ROW WITH NO DATE SORTS LAST, not first. `''` would beat every real date
 * under a plain string compare and put the least-known rows at the top.
 */
function earliestBooking(r: OrdersPanelRowDto): { tier: number; at: string } {
    const booked = r.bookedOn ?? [];
    if (booked.length === 0) return { tier: 2, at: '' };
    /* ⚠️ TIMED ONLY. A day-only booking contributes NO instant to this
       comparison — see tier 3 above. */
    const timed = booked
        .filter(b => !isDayOnly(b) && b.scheduledStartUtc)
        .map(b => String(b.scheduledStartUtc))
        .sort();
    if (timed.length > 0) return { tier: 0, at: timed[0] };
    return { tier: 1, at: '' };
}

function sortRowsForDisplay(rows: readonly OrdersPanelRowDto[]): OrdersPanelRowDto[] {
    return [...rows].sort((a, b) => {
        const da = a.effectiveDate ?? '';
        const db = b.effectiveDate ?? '';
        /* ⛔ THE EMPTY DATE GOES LAST, explicitly — see the header. */
        if (da !== db) {
            if (!da) return 1;
            if (!db) return -1;
            return da < db ? -1 : 1;
        }
        const ea = earliestBooking(a);
        const eb = earliestBooking(b);
        if (ea.tier !== eb.tier) return ea.tier - eb.tier;
        if (ea.at !== eb.at) return ea.at < eb.at ? -1 : 1;
        return a.orderNumber < b.orderNumber ? -1 : a.orderNumber > b.orderNumber ? 1 : 0;
    });
}

/**
 * ---- ⭐⭐ D6 — THE IDENTIFIERS A DRIVER MIGHT BE CARRYING (Pete, 2026-09-27) --
 *
 * > *"Any Ref numbers or PO, SO, Container, etc that has value needs to go after
 * > the Carrier/Client name on line two separated by a dash."*
 *
 * ⛔ *"THAT HAS VALUE"* IS THE WHOLE FILTER. An empty one contributes nothing —
 * not a dash, not an em-dash, not a label with a blank after it. A card for an
 * order with one reference must read `SHORR · ACME - Ref 30575419-00` and not
 * `SHORR · ACME - Ref 30575419-00 - PO  - BOL  - PRO`.
 *
 * ⭐ EACH ONE KEEPS ITS LABEL. A bare string beside another bare string is
 * exactly how `Printed 200` came to read as a quantity (§1O.5) — and these are
 * the numbers a scheduler reads back to a driver at a gate, so the one that must
 * never be ambiguous is this one.
 *
 * ⚠️ ORDER IS FIXED AND IS NOT ALPHABETICAL: customer PO, reference, container,
 * BOL, PRO. It runs most-likely-carried first (§1AG: a Camelot outbound driver
 * has a PO or reference only 25% of the time, and almost never a PRO), so a
 * truncated line loses the least useful end.
 */
function rowIdentifiers(r: OrdersPanelRowDto): string[] {
    const parts: Array<[string, string | null | undefined]> = [
        ['PO', r.customerOrderNumber],
        ['Ref', r.customerReference],
        ['Container', r.containerNumber],
        ['BOL', r.bolNumber],
        ['PRO', r.proNumber],
    ];
    return parts
        .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
        .map(([label, v]) => `${label} ${String(v).trim()}`);
}

function bookedWhere(b: OrdersPanelRowDto['bookedOn'][number] | undefined): string {
    if (!b) return '';
    const door = b.dockDoorCode ? ` on ${b.dockDoorCode}` : '';
    const clock = hhmm(b.scheduledStartUtc);
    /* ⛔ `appointmentTimeLabel` returns 'All day' or the formatted clock — and ''
       when there is no usable instant, which must NOT become ` at `. */
    const label = appointmentTimeLabel(b, clock);
    const when = !label ? '' : label === 'All day' ? ', all day' : ` at ${label}`;
    return `${door}${when}`;
}

interface OrdersPanelProps {
    facilityCode: string;
    getToken: () => Promise<string>;
    /** ⭐ §1Z.1 — the range the scheduler is already looking at. */
    fromDate: string;
    toDate: string;
    /** e.g. "this week" / "Tue, Sep 9". Rendered verbatim in the scope sentence. */
    scopeLabel: string;
    /**
     * ⭐⭐ THE LATCHED RAIL SCOPE. Pete, 2026-09-10: *"We should also search the WMS
     * Orders just like all the others, the information from the appointment searched
     * for any matching orders in the WMS."* — then: *"Only search whats loaded on in
     * the WMS panel."*
     *
     * ⛔⛔ SO IT FILTERS THE LOADED PAGE. It never re-queries the WMS, which means an
     * order outside the range on screen CANNOT be found this way — and the scope line
     * says so, because otherwise its absence reads as "no order exists for this truck".
     */
    focus?: RailFocus | null;
    /**
     * ---- ⭐⭐ WHAT THE BOARD IS SHOWING (Pete, 2026-09-25) ------------------
     * > *"it doesn't filter the drivers/client/carrier/consignee and the right
     * > side panels to those appointments"* -> *"same with all the status' too"*.
     * ⛔ A SECOND AXIS, NOT A NARROWER FOCUS. `focus` is a hover or a selection;
     * this is the board's filter set. Folding one into the other would make a
     * mouse-over change what the filters mean. See `lib/boardScope.ts`.
     * ⚠️ `undefined` MEANS NO BOUND. An empty Set is a real answer.
     */
    boardApptIds?: ReadonlySet<string>;
    /**
     * ---- ⭐⭐⭐ THE DENOMINATOR, AND IT COMES FROM THE BOARD ON PURPOSE -------
     *
     * > Pete, 2026-09-27: *"The WMS orders panel should show the XX matched XX
     * > appointments - so 15 orders in the WMS Orders panel matched how many of the
     * > total appointments (36) for the day."* → **"Three numbers: 36 appts / 12
     * > matched / 15 orders"**.
     *
     * ⛔⛔ IT IS A PROP, NOT A SECOND BACKEND COUNT, AND THAT IS THE WHOLE
     * ARGUMENT. `36` is already on screen twice — the status bar's tally and this.
     * A count this panel fetched for itself would be a SECOND SOURCE OF TRUTH for
     * one number, and the day they disagree — a refresh landing between them, a
     * range read a second apart — the scheduler has no way to know which is right.
     *   ** TWO COUNTS OF ONE THING ON ONE SCREEN IS WORSE THAN ONE COUNT, EVEN
     *      WHEN BOTH ARE CORRECT. **
     * ⭐ `Calendar.tsx` already computes it UNFILTERED for exactly this reason
     * (`totalCount`), so the ratio cannot move when somebody ticks Unloading.
     *
     * ⚠️ IT INCLUDES DOOR BLOCKS, because the board's own tally does. A block can
     * never match a WMS order, so it is a denominator that is honest about the
     * board and very slightly pessimistic about trucks. ⛔ The alternative — a
     * number that excludes them — would make this panel disagree with the tally
     * eight pixels away, which is the failure above. The tooltip says which it is.
     *
     * ⚠️ `undefined` MEANS THE CALLER DID NOT SAY, and the ratio is not drawn at
     * all. ⛔ Never defaulted to 0: a `12 / 0` would be arithmetic nobody asked for.
     */
    apptsInRange?: number;
    /**
     * ---- ⭐⭐ THE PAGE'S REFRESH BUTTON REACHES THIS PANEL (Pete, 2026-09-27) ---
     *
     * > *"the Refresh button right below the sprocket should do a screen refresh …
     * > it refreshes the panels (like it does now), refreshes the driver, clients,
     * > carrier and consignees and the calender."*
     *
     * ⛔⛔ IT DID NOT REACH THIS ONE. The toolbar bumps `refreshKey`, and the arrivals
     * panel, Labour, Communications and History all take it. **This panel never
     * declared it** — so Refresh moved every list on screen except the orders, and
     * the one thing that hid it was this panel's own 60-second poll: wait a minute
     * and it catches up anyway, so it reads as slow rather than as unwired.
     *   ** A COMPONENT THAT SELF-HEALS ON A TIMER HIDES THE FACT THAT NOTHING IS
     *      TELLING IT ANYTHING. **
     *
     * ⚠️ OPTIONAL WITH A DEFAULT, like its three siblings — a caller that never
     * refreshes must not be forced to invent a number.
     */
    refreshKey?: number;
    /**
     * ---- ⭐⭐⭐ HOVER SCOPES, CLICK LATCHES (Pete, 2026-09-27) ----------------
     *
     * > *"when you hover an appointment or hover the WMS orders record, it needs to
     * > highlight appointment or order record associated to it"* … *"when you click on
     * > an order or an appointment … the appointment only shows in the calendar and all
     * > the panels show the specific appointment information including the one order."*
     *
     * ⛔⛔ THIS PANEL REPORTED **NOTHING**. Its rows' hover is pure CSS `:hover`, and
     * `rail-scope-guard` measures *"0 call(s)"* for it and still passes, because that
     * check only reads the className. That was not an oversight — see the row's own
     * note: a hover that re-scopes the list containing the hovered row is a loop, and
     * this list is the one a WMS hover would re-scope. It was removed at cost on
     * 2026-09-21.
     *
     * ⭐ WHAT MAKES IT SAFE NOW is `CheckInPanel`'s `railHover` flag: the rail that
     * raised the hover reads the COMMITTED scope instead of the preview, so this list
     * does not rebuild under the cursor. ⛔ Do not wire these to anything that feeds
     * `focus` back into this panel without that flag.
     *
     * ---- ⚠️ IT SENDS THE **BOOKINGS**, NOT AN ID ------------------------------
     *
     * An order can be on several trucks — *"an order can obviously have multiple trucks
     * to fulfill it but only 1 truck per appointment"* — so there is no single id to
     * send. `CheckInPanel` builds the focus with `focusFromBookedOrder`, which covers
     * all of them and refuses to name one when there are several.
     *
     * ⚠️ AN UNBOOKED ORDER STILL REPORTS, with an empty array. The focus that comes
     * back is not scopeable and the rails say WHY they are empty — which is the point.
     * ⛔ Reporting nothing would make an unbooked row look like a dead one.
     */
    onHoverOrder?: (
        orderNumber: string | null,
        bookedOn: readonly { appointmentId: string; appointmentNumber: string | null }[],
    ) => void;
    onChooseOrder?: (
        orderNumber: string,
        bookedOn: readonly { appointmentId: string; appointmentNumber: string | null }[],
    ) => void;
    onClearFocus?: () => void;
    /**
     * ⭐ Open the New Appointment dialog prefilled from this order.
     *
     * ⚠️ OPTIONAL. The panel is useful read-only, and a missing handler must hide
     * the button rather than render one that does nothing.
     */
    onCreateFromOrder?: (row: OrdersPanelRowDto) => void;
    /**
     * ⭐ D3 / Decision 238 (Pete, 2026-09-28: *"Match on each unmatched order"*).
     * A Match button on an order with no appointment opens the page's picker of
     * the day's appointments; choosing one attaches it through the SAME path as a
     * drag (A5), replace popup included.
     * ⚠️ OPTIONAL: absent, the button is not drawn.
     */
    /** ⭐ The row travels too, so the Match screen can show the order beside each load (Decision 241). */
    onMatchOrder?: (order: WmsOrderDragKey, row: OrdersPanelRowDto) => void;
}

/**
 * ⭐⭐ D3 — THE THREE VIEWS (Decision 237). They replace `board | browse`.
 *   matched   — orders on an appointment the board is showing (was `board`)
 *   unmatched — orders on NO appointment at all (`bookedOn` empty)
 *   all       — every order the WMS dates in the range (was `browse`)
 * ⚠️ An order matched only to an appointment the board is NOT showing is in
 * `all` and in neither of the other two: it is not unmatched, and it is not
 * on this board.
 */
type PanelView = 'matched' | 'unmatched' | 'all';

/* ⛔⤴ ~~`viewLabel`~~ — the word beside the icons, removed 2026-09-28 (Pete: *"Lose
   the 'Matched' next to the icon"*). `VIEW_BUTTONS[].label` still names each view for
   the tooltip and for screen readers. */

/**
 * ⛔ F108 (2026-09-29) — THE VIEW BUTTONS ARE SUPPRESSED FOR NOW. Pete: *"lets suppress
 * the WMS Orders panel no matched and all scheduled icon buttons for now too. Want to
 * see what that looks like with just having the NO WMS bubble up top"* → popup *"Hide
 * all three buttons"*. The panel shows the Matched view; Not matched and All scheduled
 * live on in the Match screen, beside its order search. Flip this to bring them back.
 */
export const ORDERS_VIEW_BUTTONS_ENABLED = false;

const VIEW_BUTTONS: readonly {
    key: PanelView; label: string; icon: typeof Link2; title: (scope: string) => string;
}[] = [
    { key: 'matched', label: 'Matched', icon: Link2,
      title: () => 'Matched — the orders on the appointments this board is showing, whatever their own date, type, status or source.' },
    { key: 'unmatched', label: 'Not matched', icon: Unlink,
      title: (scope) => `Not matched — orders scheduled for ${scope} that are on no appointment. Drag one onto a load, or press Match.` },
    { key: 'all', label: 'All scheduled', icon: CalendarDays,
      title: (scope) => `All scheduled — every order the WMS dates for ${scope}, narrowed by the boxes below.` },
];

export function OrdersPanel({
    facilityCode, getToken, fromDate, toDate, scopeLabel, onCreateFromOrder, onMatchOrder,
    /**
     * ⛔⤴ ~~`onClearFocus`~~ NO LONGER DESTRUCTURED (increment 3, 2026-09-19). The
     * per-panel `ALL` button is gone; ONE master reset at the top of the rail clears
     * the focus for all of them. ⚠️ The PROP stays in the interface so the page's
     * wiring is unchanged and so a panel can regain its own control without a
     * signature change — but nothing here may render one, or there would be five
     * controls for one action again.
     */
    focus = null, boardApptIds, apptsInRange, refreshKey = 0, onHoverOrder, onChooseOrder,
}: OrdersPanelProps) {
    const [open, setOpen] = useState(false);
    /**
     * ⛔⛔ ~~THE ACCORDION~~ REVERTED 2026-09-10, SAME DAY, BY PETE: *"now they
     * don't work. none of them expand. Put it back."*
     *
     * ⚠️ THE FAULT WAS A RENDER/COMMIT RACE, not the idea. Clicking a chevron set
     * `open = true`; on THAT render the rail had not been told yet, so `openCodes`
     * was still empty and the yield signal read TRUE for the panel that had just
     * opened. Both effects then ran in the same commit — the report went up and the
     * panel closed itself. It closed on the way open, every time.
     *
     * ⛔⛔ AND THE SIMULATION THAT PASSED IT TESTED THE WRONG THING. It modelled
     * report → reduce → read as a settle-to-fixpoint loop and converged in two
     * passes. React captures the yield value during RENDER, before the report has
     * updated the context — the simulation had no render/commit ordering in it at
     * all, so it could not have caught this. §4.14, in the check written to prove
     * the accordion safe.
     *
     * ⭐ WHEN IT COMES BACK: report SYNCHRONOUSLY from the click, alongside
     * `setOpen`, so the next render already has the new `openCodes` and the stale
     * window does not exist. An effect is the wrong place for it.
     */
    /* ⭐ DECISION 193 — the two-panel cap. `useRailSlotClose` reports this panel's
       state upward AND closes it when the rail says a third panel pushed it out.
       ⛔ The policy is `nextRailSlots` in lib/railSlot.ts, proven by
       tools/rail-slot-test.cjs — not reasoned about here. */
    useRailSlotClose('ORDERS', open, setOpen);


    const [data, setData] = useState<OrdersPanelDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /** ⭐ The filter strip, behind the search icon — the shape the other three rails use. */
    const [showFilters, setShowFilters] = useState(false);

    /**
     * ⛔⛔ `null` IS "NO FILTER"; A SET IS "ONLY THESE" (§1R.10). An empty set is
     * a real state — every box unticked — and it shows nothing, honestly.
     */
    const [sources, setSources] = useState<Set<SourceKey> | null>(null);
    /**
     * ⛔ A SET, LIKE THE SOURCES (Pete: *"Should have checkboxes in search for
     * inbound and outbound"*). §1R.10 — `null` is no filter, an empty set keeps
     * nothing, and those are different states.
     */
    const [directions, setDirections] = useState<Set<'INBOUND' | 'OUTBOUND'> | null>(null);
    /**
     * ---- ⭐ TWO BOOKING BOXES, DELIBERATELY DIFFERENT DATE SCOPES ----------
     *
     * Pete, 2026-09-09: *"How about Booked Today, Never Booked"*.
     *
     * ⛔⛔ THEY ARE NOT COMPLEMENTS AND MUST NEVER BE A TOGGLE PAIR. `unbooked`
     * means "no appointment on ANY date, ever"; `bookedInRange` means "has one
     * inside the range on screen". ⚠️ An order booked for next Tuesday is
     * excluded by BOTH — correct, and the reason Pete chose this over
     * range-scoping both: the never-booked warning is what stops a
     * double-booking, and range-scoping it would throw that away.
     */
    /* ⛔⤴ ~~`unbookedOnly` / `bookedInRange` state~~ — REMOVED 2026-09-28, B6. Pete:
       *"Lets remove the Booked Sep 25, Never booked and other sites options (or
       suppress)"*. D3's three views replace them: *Not matched* is on no appointment,
       *Matched* is on this board. The request no longer carries either flag; the
       server still accepts them, unused. The note above is kept for the history. */
    /**
     * ---- ⛔⛔ OFF BY DEFAULT, AND THAT DEFAULT IS THE FIX (#357) -----------
     *
     * The panel used to be bounded by COMPANY only — no facility filter at all —
     * so a Brighton scheduler saw Denver's, BB2's, Nashville's and Reno's
     * freight as one list. Since Brighton has no orders of its own, EVERY row on
     * his screen belonged to somebody else, labelled with a bare code that read
     * as a quantity.
     *
     * ⭐ Other sites stay reachable — Pete's ruling stands, *"Show them, sorted
     * below, labelled with the site"* — but you have to ask for them.
     */
    /* ⛔⤴ ~~`includeOtherSites` state~~ — REMOVED 2026-09-28, B6 (see above). The
       dated list stays this site's (the server's default when the flag is absent),
       and a typed search keeps the server's own rule for sites. */

    /**
     * ---- ⭐⭐ WHICH OF THE PANEL'S TWO JOBS IS ON SCREEN (SPEC §5.1) --------
     *
     * > Pete, 2026-09-26, choosing between three shapes: *"a segmented control
     * > above the list"*.
     *
     * ⛔ A VIEW, NOT A FILTER, AND THE DISTINCTION IS THE WHOLE REASON IT IS
     * NOT A CHECKBOX. The two boxes below — "Booked {range}" and "Never booked"
     * — are filters, they are NOT complements, and this file already carries a
     * warning that they must never be treated as a toggle pair. An order booked
     * for next Tuesday is excluded by both, correctly. Repurposing one of them
     * as the switch would have given that order no view at all.
     *
     *   ** A CONTROL THAT SELECTS BETWEEN TWO LISTS IS NOT THE SAME KIND OF
     *      THING AS ONE THAT SHORTENS ONE LIST. **
     *
     * ⭐ D3 (2026-09-28, Decision 237): NOW THREE VIEWS — `matched` (was 'board'),
     * `unmatched` (new: on no appointment) and `all` (was 'browse'). See `PanelView`.
     * The reasoning below is unchanged; only the number of states moved.
     *
     * 'board'  — the orders on the appointments in front of you. The default,
     *            because SPEC §1's defect was a booked order vanishing from a
     *            panel the scheduler was using to check that very truck.
     * 'browse' — the dated WMS list this panel has always shown, with every
     *            checkbox still meaning exactly what it says, inside it.
     *
     * ⚠️ THE SERVER SENDS BOTH POPULATIONS EITHER WAY. This chooses which is
     * rendered, and `ordersPanelRepository` now guarantees a board-booked order
     * is in the payload whatever its own date, type, status or source says.
     */
    const [view, setView] = useState<PanelView>('matched');

    /**
     * ---- ⭐⭐ THE STATUS FILTER (#359), AND `null` IS THE IMPORTANT STATE ---
     *
     * > Pete, 2026-09-09: *"Think we need to narrow the search on certain WMS
     * > order status's too."* … *"That is why we are getting so many"*
     *
     * ⛔⛔ `null` MEANS "THE SCHEDULER HAS NOT TOUCHED THESE BOXES", and the
     * request then OMITS the parameter entirely. That is not tidiness — it is
     * what lets the server tell a DEFAULT from a CHOICE, which is the whole of
     * §1Z.3. ⚠️ If this were seeded with `new Set(['OPEN'])` the panel would
     * always spell the default out, the server could no longer distinguish it,
     * and a typed search would stay silently narrowed to open orders — reporting
     * "nothing found" for a container whose order is marked Posted.
     *
     * ⛔ THERE IS NO BOX FOR "UNRECOGNISED" AND THAT IS DELIBERATE. A status
     * nobody has classified is always shown; making it a checkbox would invite
     * turning it off, and the whole point is that we do not know what those
     * orders are yet. The (i) explains it instead.
     */
    const [statusGroups, setStatusGroups] = useState<Set<StatusGroupKey> | null>(null);
    /** ⭐ The (i) — Pete: *"put the mapping in laman terms in an (i) on the WMS Panel"*. */
    const [showLegend, setShowLegend] = useState(false);

    /** ⭐ The search box, inside the collapsible strip. Empty = the dated list. */
    const [q, setQ] = useState('');
    const [submittedQ, setSubmittedQ] = useState('');

    const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
    const [detailFor, setDetailFor] = useState<OrdersPanelRowDto | null>(null);

    const reqIdRef = useRef(0);
    /**
     * ---- ⭐⭐ STOP, NOT CLEAR (Pete, 2026-09-22) -----------------------
     *
     * > Pete: *"Clear should be the stop button. Don't need a clear button
     * > for the small text."*
     *
     * ⭐ HE IS RIGHT ABOUT BOTH HALVES. Emptying a one-line box is two
     * keystrokes and submitting an empty query already returns the dated
     * list - `onSubmit` sets `submittedQ` to the trimmed value with no
     * minimum, so nothing is stranded by the button going away.
     * ⛔ A RUNNING SEARCH, by contrast, had no way out at all.
     *
     * ⚠️ IT INVALIDATES THE RUN, it does not merely hide the spinner. `load`
     * already guards every setState on this id.
     *   ** A CANCEL THAT SILENTLY LETS THE OLD ANSWER WIN IS WORSE THAN NO
     *      CANCEL: IT LOOKS LIKE THE SEARCH RAN TWICE. **
     */
    function cancelSearch() {
        reqIdRef.current += 1;
        setLoading(false);
    }

    const load = useCallback(async () => {
        /**
         * ⛔⛔ THE LAST REQUEST TO BE *SENT* WINS, NOT THE LAST TO ARRIVE. A
         * scheduler changing a filter twice quickly fires two reads, and without
         * this the slower first one can land second and paint a list that does
         * not match the boxes on screen — with nothing to say so.
         */
        const id = ++reqIdRef.current;
        setLoading(true);
        try {
            const out = await getPanelOrders(await getToken(), {
                facilityCode, from: fromDate, to: toDate, scopeLabel,
                directions: directions === null ? undefined : [...directions],
                sources: sources === null ? undefined : [...sources],
                /* ⛔ B6 — unbookedOnly / bookedInRange / includeOtherSites are no longer
                   sent (absent = the server's default, which is what `false` meant). */
                /* ⛔ undefined WHEN UNTOUCHED - see the note on the state. Sending
                   the default explicitly would break §1Z.3 at the server. */
                statusGroups: statusGroups === null ? undefined : [...statusGroups, 'UNCLASSIFIED'],
                q: submittedQ || null,
            });
            if (id !== reqIdRef.current) return;
            setData(out);
            setError(null);
        } catch (err) {
            if (id !== reqIdRef.current) return;
            /* ⭐ THE FEED FAILING IS NOT THE FEED BEING EMPTY, and the two must
               never render the same way — that confusion is what the
               Communications panel was built to end. */
            setError(err instanceof Error ? err.message : String(err));
        } finally {
            if (id === reqIdRef.current) setLoading(false);
        }
    }, [facilityCode, getToken, fromDate, toDate, scopeLabel, directions, sources,
        statusGroups, submittedQ]);

    /* ⚠️ ONLY WHILE OPEN. A folded panel that polls costs every scheduler a
       request a minute for a list nobody is reading — the cost §1AD's own note
       says to fix this way if it ever bites. */
    /* ⭐ `refreshKey` IS A DEPENDENCY, which is the whole wiring: the page bumps it,
       this effect re-runs, the list re-reads AND the poll timer restarts from now —
       so Refresh also buys a full minute before the next automatic read, instead of
       firing one two seconds later. ⛔ A bare `void load()` in a second effect would
       have refreshed the list and left the old interval running.
         ** A POLL THAT IGNORES A MANUAL REFRESH ASKS THE SERVER TWICE FOR ONE
            ANSWER. ** */
    useEffect(() => {
        if (!open) return;
        void load();
        const t = setInterval(() => { void load(); }, POLL_MS);
        return () => clearInterval(t);
    }, [open, load, refreshKey]);

    const loadedRows = data?.rows ?? [];
    /* ⭐ THE HOVER SCOPE, APPLIED OVER WHAT IS ALREADY LOADED. `rows` keeps its name
       so every consumer below — the count, the cap line, the empty state, the map —
       narrows together; a scope applied to the list but not the count is how a panel
       says "12" above three rows. */
    const ordersBlockedReason = focus ? focusBlockedReason(focus, 'orders') : null;
    const focusedRows = !focus
        ? loadedRows
        : (ordersBlockedReason ? [] : loadedRows.filter(r => ordersMatchesFocus(r, focus)));
    /**
     * ---- ⛔⛔ AND AN UNBOOKED ORDER SURVIVES, WHICH IS MOST OF THIS PANEL ----
     *
     * ⚠️ THIS PANEL EXISTS TO FIND ORDERS TO BOOK. Every unbooked order has no
     * appointment, so a strict board bound would empty it of exactly what it is
     * for the moment a scheduler touches a filter.
     *   ** A PANEL WHOSE PURPOSE IS THE UNMATCHED CANNOT BE SCOPED BY MATCHES. **
     *
     * ⛔ `onBoardAny`, NOT `onBoard`. `bookedOn` is a LIST — an order can be
     * booked across two appointments — and `ordersMatchesFocus` already reads it
     * as one. A scalar test would take the first booking and hide an order the
     * board is showing under its second.
     *
     * ⚠️ `rows` KEEPS ITS NAME so the count, the cap line, the empty state and the
     * map all narrow together — the rule this block already opens with.
     */
    const boardScoped = focusedRows.filter(r => onBoardAny(boardApptIds, (r.bookedOn ?? []).map(b => b.appointmentId)));
    const boardHidden = focusedRows.length - boardScoped.length;

    /**
     * ---- ⭐⭐ HOVER FORCES THE BOARD VIEW, AND DOES NOT MOVE THE CONTROL -----
     *
     * > Pete, 2026-09-26: *"when you hover an appointment, even if the toggle is
     * > on to unbooked orders, the board orders flips and highlights."*
     *
     * ⭐ THE FOCUS LATCHES; THE VIEW FOLLOWS IT. `railFocus` is explicit that
     * hovering SETS the scope and it stays until ALL is clicked — Pete chose
     * that on 2026-09-10 and the ask for an ALL icon is the evidence for it. So
     * this is not a mouse-over effect: the flip lasts exactly as long as the
     * focus does.
     *
     * ⛔ `view` IS NOT WRITTEN TO. The scheduler's own choice survives the
     * detour and comes back when ALL clears the focus. Calling `setView` here
     * would make a hover silently edit a control the scheduler set — and they
     * would have no way to know the button moved under them.
     *
     *   ** AN OVERRIDE THAT REWRITES THE SETTING IT OVERRODE IS NOT AN
     *      OVERRIDE. IT IS AN EDIT NOBODY MADE. **
     */
    /* ⛔ With the buttons suppressed nothing can switch the view, so it is pinned to
       Matched rather than trusting that `view` never changed. */
    const effectiveView: PanelView = (focus || !ORDERS_VIEW_BUTTONS_ENABLED) ? 'matched' : view;

    /**
     * ⛔ NOT THE SAME TEST AS `onBoardAny`, AND THE DIFFERENCE IS THE POINT.
     * `onBoardAny` lets a row that names NO appointment through — *"a row that
     * names none is not the board's to hide"*, which is what keeps unbooked
     * orders in the browse. This one requires a booking the board is showing,
     * because that is what the word "board" means on the control.
     *
     * ⚠️ WITH NO BOUND (`boardApptIds` undefined) THE QUESTION WAS NEVER ASKED,
     * so the honest answer is "has any booking at all" rather than "everything"
     * — the latter would make the board view identical to the browse on any
     * screen not yet wired up, and nothing would say so.
     */
    /** ⭐ D3 — matched to NO appointment anywhere. */
    const isUnmatched = (r: OrdersPanelRowDto): boolean =>
        (r.bookedOn ?? []).filter(b => !!b.appointmentId).length === 0;

    const isOnBoard = (r: OrdersPanelRowDto): boolean => {
        const ids = (r.bookedOn ?? []).map(b => String(b.appointmentId)).filter(Boolean);
        if (ids.length === 0) return false;
        return boardApptIds ? ids.some(id => boardApptIds.has(id)) : true;
    };

    /* ⭐ D8 — sorted for the eye, not for the cap. `sortRowsForDisplay` and its
       header explain why the screen and the SQL deliberately disagree.
       ⚠️ `rows` KEEPS ITS NAME and its LENGTH, so the count, the cap line, the
       empty state, `browseOnly` and the map all still agree. */
    const viewRows = effectiveView === 'matched' ? boardScoped.filter(isOnBoard)
        : effectiveView === 'unmatched' ? boardScoped.filter(isUnmatched)
        : boardScoped;
    /**
     * ---- ⭐⭐ B5 — WHAT IS TYPED NARROWS THE LOADED ROWS FIRST (2026-09-28) ------
     *
     * > Pete: *"Search should search the loaded items first, then out to database"*.
     *
     * ⭐ `localNeedle` is the box's text while it differs from the last SUBMITTED
     * search: typing filters what is already on screen, with no request. Enter is
     * still the database search (every date); once submitted, the server's answer is
     * the list and nothing narrows it again.
     * ⛔ THE CAP: the loaded list is dated and capped, so a local miss is worded as
     * "none of the N loaded", never "not found" — see `orderLocalSearch.ts`.
     * ⚠️ `browseOnly` is taken BEFORE this filter: it counts what the VIEW holds back,
     * and a half-typed number must not read as orders missing from the board.
     */
    const typedQ = q.trim();
    const localNeedle = typedQ && typedQ !== submittedQ ? typedQ : '';
    const rows = sortRowsForDisplay(
        localNeedle ? viewRows.filter(r => loadedOrderMatches(r, localNeedle)) : viewRows,
    );
    /**
     * ---- ⭐ THE HOVER IS RELEASED WHEN ITS ROW GOES (2026-09-28) --------------
     *
     * ⛔ `onMouseLeave` never fires on a row that UNMOUNTS under the cursor — after
     * an attach reloads the list, or a popup opens over it and the list changes
     * behind. The preview it set would then outlive the row and hold every panel.
     * So the panel remembers which row it reported, and releases it when that row
     * is no longer on screen, and when the panel itself goes.
     */
    const hoveredKeyRef = useRef<string | null>(null);
    const rowKeyOf = (r: OrdersPanelRowDto) => `${r.wmsSource}:${r.orderType}:${r.orderNumber}`;
    const reportHover = (r: OrdersPanelRowDto | null) => {
        if (!r) {
            if (hoveredKeyRef.current === null) return;
            hoveredKeyRef.current = null;
            onHoverOrder?.(null, []);
            return;
        }
        hoveredKeyRef.current = rowKeyOf(r);
        onHoverOrder?.(r.orderNumber, r.bookedOn ?? []);
    };
    const onScreenKeys = rows.map(rowKeyOf).join('|');
    useEffect(() => {
        const k = hoveredKeyRef.current;
        if (k !== null && !onScreenKeys.split('|').includes(k)) {
            hoveredKeyRef.current = null;
            onHoverOrder?.(null, []);
        }
    }, [onScreenKeys, onHoverOrder]);
    useEffect(() => () => {
        if (hoveredKeyRef.current !== null) onHoverOrder?.(null, []);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ⛔⤴ ~~`browseOnly`~~ — the count behind *"N orders are not on it — show all
       scheduled"*, removed with that sentence (Pete, 2026-09-28). */

    /**
     * ⭐ THE VIEW ICONS — rendered in the HEADER (see there). ⚠️ Pressing one also
     * OPENS the panel: on a folded card it would otherwise change a list nobody can see.
     * ⛔ While a focus is latched they are disabled and SAY why in the tooltip — the
     * words that used to sit beside them ("held by the hover · returns to …") are gone.
     */
    /* ⚠️ Suppressed → an EMPTY `ml-auto` spacer, not nothing: the toggle carries the
       `ml-auto` that pushes the count and ↻ to the right (note below). */
    const viewToggle = !ORDERS_VIEW_BUTTONS_ENABLED ? <div className="ml-auto" aria-hidden="true" /> : (
        <div className="ml-auto flex shrink-0 items-center gap-0.5 self-center rounded border px-0.5 py-0.5">
            {VIEW_BUTTONS.map(b => {
                const Icon = b.icon;
                const on = effectiveView === b.key;
                return (
                    <button
                        key={b.key}
                        type="button"
                        disabled={!!focus}
                        aria-pressed={on}
                        title={focus
                            ? `${b.label} — the panel is scoped to what you chose. Press Ⓡ (or click it again) to choose a view.`
                            : b.title(scopeLabel)}
                        onClick={() => { setView(b.key); setOpen(true); }}
                        className={'flex items-center justify-center rounded p-1 transition-colors '
                            + (on ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground')
                            + (focus ? ' cursor-not-allowed opacity-60' : '')}
                    >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="sr-only">{b.label}</span>
                    </button>
                );
            })}
        </div>
    );

    /**
     * ---- ⛔⛔ THE DOT MEANS "THE LIST IS NARROWED", NOT "YOU TOUCHED THIS" ---
     *
     * ⚠️ Pete, 2026-09-09: *"What does the orange dot above the search mean on
     * WMS Orders panel?"* — asked with all three source boxes TICKED, which
     * excludes nothing, and the dot lit anyway.
     *
     * ⛔ THE OLD TEST WAS `sources !== null`, true the moment a box is touched —
     * even after ticking everything back on. The dot exists to warn that a
     * COLLAPSED strip is hiding an incomplete list; one that lights when nothing
     * is filtered teaches the reader to ignore it, and then it is worthless on
     * the day the list really is short. ⭐ An indicator is a claim (§1R.17).
     *
     * ⚠️ NOTE THE ASYMMETRY WITH THE REQUEST, AND IT IS DELIBERATE. The server
     * still distinguishes `null` from a full set — §1R.10, and that is about the
     * QUERY. This is about the DOT, and a full set narrows nothing.
     */
    const sourcesNarrow = sources !== null && sources.size < ALL_SOURCE_KEYS.length;
    const directionsNarrow = directions !== null && directions.size < 2;
    const filtersActive = sourcesNarrow || directionsNarrow
        || submittedQ.length > 0;

    /* ⛔⤴ ~~`todayIso` / `rangeWord`~~ — removed with the "Booked {range}" box (B6).
       The rule they carried — "the word 'today' has to be true" — lives on in the
       view tooltips, which name the scope label instead. */

    /**
     * ⛔ EVERY BOX UNTICKED IN A GROUP MAKES A MATCH IMPOSSIBLE, and the panel
     * must say THAT rather than "Nothing due". §1R.10 keeps the query honest;
     * this keeps the SCREEN honest. Pete read an empty panel as broken
     * checkboxes — *"Check boxes not working properly ... not showing any
     * results"* — because "Nothing due today." is a flat lie when plenty is due
     * and the filter is excluding all of it.
     */
    const excludedByFilter =
        (directions !== null && directions.size === 0) ? 'direction'
      : (sources !== null && sources.size === 0) ? 'source'
      : null;


    const toggleSource = (k: SourceKey) => {
        setSources(prev => {
            /* ⭐ FIRST CLICK MEANS "ONLY THIS ONE". Starting from null (no filter)
               and unticking one box would otherwise leave every other box looking
               ticked while the filter said nothing — §1R.12, a picker says WHICH,
               never WHETHER. */
            const base = prev ?? new Set<SourceKey>(ALL_SOURCE_KEYS);
            const next = new Set(base);
            if (next.has(k)) next.delete(k); else next.add(k);
            /* ⚠️ Back to everything ticked = back to NO FILTER, so the request
               stops carrying a sources param at all. */
            return next.size === ALL_SOURCE_KEYS.length ? null : next;
        });
    };

    const sourceTicked = (k: SourceKey) => sources === null || sources.has(k);

    return (
        <div className="flex min-h-0 flex-col">
            {/* ⭐ THE HEADER IS NOT INSIDE ANY SCROLLER — see CommunicationsPanel's
                note. Only the body below scrolls, which is structural rather than
                a `sticky` promise about whichever ancestor happens to scroll. */}
            {/* ⭐ F59 (Pete, 2026-09-28): *"Orders lost its chevron and the spacing needs to be
                fixed."* With the view icons in the header the row ran out of width and the
                TITLE shrank — the chevron went first. Now the title never shrinks, the gaps
                are tighter (gap-1), and the scope label is what gives way, with an ellipsis
                and its full text in the tooltip. */}
            <div className={`shrink-0 bg-card flex w-full items-center gap-1 px-3 py-2 ${SECTION_HEADER}`}>
                <button
                    type="button"
                    onClick={() => setOpen(o => !o)}
                    className="flex shrink-0 items-center gap-2 text-left"
                    aria-expanded={open}
                >
                    {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                    {/**
                      * ⭐ THE PAPERWORK FAMILY, IN BLACK AND WHITE. Pete asked for a
                      * colour like Labor's purple and Communications' emerald, then
                      * reversed it the same minute: *"Actually, lets keep it black
                      * and white like it is."* ⛔ The reversal is the instruction.
                      *
                      * ⚠️ ~~`Printer`~~ WAS WRONG. Pete: *"WMS Orders has a print icon
                      * next to it. It should be the same icon as Scanned Paperwork"* …
                      * *"Maybe with a pen writing on it to distinguish"*. `FilePen` is
                      * a page with a pen across it, distinguishable at 14px, which
                      * `FilePenLine`'s baseline stroke is not.
                      *
                      * ⛔ ~~"Scanned Paperwork uses `FileText`"~~ — NOT SINCE 2026-09-10.
                      * Pete: *"Maybe we use a different icon for the scanned paperwork -
                      * seems to similiar to others."* That tray is `Inbox` now, and
                      * "same family" was exactly the complaint. ⚠️ Recorded here rather
                      * than left standing, because a comment describing another
                      * component's icon is a claim that rots the moment that component
                      * changes.
                      *
                      * ---- ⭐ AND THE ICON CARRIES A COLOUR (2026-09-10) --------------
                      *
                      * > Pete: *"Can we make WMS Orders another color too so we don't
                      * > have two black ones?"* … *"Meaning the icon"*.
                      *
                      * ⭐ THE COLOUR IS ON THE ICON ONLY, NOT THE TITLE. Every rail
                      * header reads the same weight and colour in its text; the glyph is
                      * what a scheduler finds the panel by when four of them are stacked
                      * and folded.
                      *
                      * ⚠️ INDIGO BECAUSE THE OTHERS ARE SPOKEN FOR: cyan is the paperwork
                      * tray's ATTACHED mode, emerald means signed on a document row,
                      * amber means unmatched on an arrival, violet is a dedication on the
                      * grid. ⛔ A panel's identity colour must not collide with a colour
                      * that means a STATUS somewhere else on the same screen.
                      */}
                    <FilePen className="h-3.5 w-3.5 text-indigo-700" />
                    {/* ⭐ Matches the FilePen glyph above — see `PaperworkSliver`'s note. */}
                    {/**
                      * ---- ⭐⭐ THE TITLE IS **"Orders"**, NOT "WMS Orders" (2026-09-27) --
                      *
                      * > Pete: *"Lets Change WMS Orders to just WMS too so we have room for
                      * > the counter"* → on seeing it: *"Actually, lets change it again. WMS
                      * > to Orders instead of WMS Orders. I think Orders fits better"*
                      *
                      * ⛔ §0.3 — THE FIRST SHORTENING IS RECORDED, NOT ERASED. It read
                      * ~~`…text-indigo-700" title="WMS Orders">WMS<`~~ for the length of one
                      * build. ⭐ THE SECOND ASK IS THE INSTRUCTION, and the first one's
                      * REASON is unchanged and still governs — only the word moved.
                      *
                      * ⭐ THE COUNTER IS WHY EITHER OF THEM HAPPENED. This header is ONE FLEX
                      * ROW carrying **name · 🔍 · (scope) · count · ↻** inside a rail ~300px
                      * wide, and the count is about to stop being `rows.length` and become
                      * THREE numbers — appointments / matched / orders (SPEC §5, Pete's
                      * *"Three numbers: 36 appts / 12 matched / 15 orders"*). Four characters
                      * of title buy the room for them.
                      *   ** A ROW THAT FITS IS NOT THE SAME ROW ONCE ONE OF ITS
                      *      OCCUPANTS TRIPLES. **
                      *
                      * ⚠️ "Orders" DROPS THE ONLY WORD THAT SAID WHERE THE ROWS COME FROM —
                      * this panel reads Camelot and Deposco, and the name no longer says so.
                      * ⭐ ON THE RAIL THAT IS THE RIGHT TRADE, and it is why "Orders" fits
                      * better than "WMS" did: the four panels beside it are named for what
                      * they HOLD — Paperwork, Labor, Communications, History — and this was
                      * the odd one out, named for the system it queries.
                      *   ** A PANEL IN A LIST OF PANELS IS NAMED BY ITS CONTENTS. THE SOURCE
                      *      IS A DETAIL FOR WHOEVER ASKS, WHICH IS WHAT THE TOOLTIP IS. **
                      *
                      * ⛔ §0.3 — THE ORIGINAL IS RECORDED TOO. It read, verbatim:
                      *
                      *     ~~<span …>WMS Orders</span>~~
                      *
                      * and that name is Pete's own from 2026-09-09 (*"The new Panel should
                      * be called WMS Orders"*), which is why it survives in the `title`
                      * rather than disappearing: the short form is what fits, the long form
                      * is what it means.
                      *
                      * ⚠️ `dock.RailPanel` STILL SAYS "WMS Orders" AND IS LEFT ALONE.
                      * Migration 175 seeded that label and it drives the **Right-Side
                      * Toolbar admin screen's** column heading, not this line. ⛔ A
                      * migration that has run anywhere is immutable (§3B), so changing it
                      * would be a NEW migration for an admin-screen word Pete did not ask
                      * about — and the two are allowed to differ: one names the panel in a
                      * list of panels, the other is a heading competing for 300 pixels.
                      */}
                    <span className="text-sm font-medium text-indigo-700" title="WMS Orders — Camelot and Deposco">Orders</span>
                    {loading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                </button>

                {/* ⛔ OUTSIDE the title button — a <button> inside a <button> is
                    invalid HTML and the inner one is silently unclickable. */}
                <button
                    type="button"
                    onClick={() => {
                        /* ⛔ IT OPENS THE PANEL TOO. The strip lives inside {open &&
                           ...}, so on a folded card this would set a flag nobody
                           could see — the bug Pete found on Labor and Comms. */
                        const next = !showFilters;
                        setShowFilters(next);
                        if (next) setOpen(true);
                    }}
                    aria-expanded={showFilters}
                    aria-label={showFilters ? 'Hide search and filters' : 'Show search and filters'}
                    title={filtersActive && !showFilters
                        ? 'Filters are on - open to see or clear them'
                        : showFilters ? 'Hide search and filters' : 'Search and filter'}
                    className="relative shrink-0 self-center rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                    <Search className="h-3.5 w-3.5" />
                    {/* ⭐ THE AMBER DOT IS WHAT MAKES HIDING THE STRIP HONEST. A
                        filter on behind a closed strip is invisible, and an
                        invisible filter makes a short list look like a quiet day. */}
                    {filtersActive && (
                        <span aria-hidden="true"
                              className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full bg-amber-500" />
                    )}
                </button>

                {/**
                  * ---- ⭐⭐ ONE HEADER ORDER FOR ALL FIVE RAILS (Pete, 2026-09-19) -----
                  *
                  * ⭐ *"the search icon and the (client) are all inconsistent on the
                  * panels. They should all be like Communications. Search Icon, the
                  * client then total"*.
                  *
                  * ⛔ THE ORDER IS: **name · 🔍 · (scope) · count · ↻** — Communications
                  * is the reference because it is the one Pete named, not because it was
                  * first. Orders and Labor each drifted a different way, which is what a
                  * layout copied by hand four times does.
                  *
                  * ⛔⛔ AND THIS LABEL MOVED **OUT OF THE TITLE BUTTON**, which was not
                  * cosmetic. It sat between the panel name and the spinner INSIDE the
                  * collapse toggle, so clicking the thing that tells you what you are
                  * scoped to FOLDED THE PANEL. ** AN INDICATOR INSIDE A CONTROL INHERITS
                  * THAT CONTROL'S CLICK. ** Communications and Labor already had it
                  * outside; only this one did not, and nothing said so.
                  */}
                <span
                    title={focus ? `Showing only ${focusBasisLabel(focus)}` : 'Showing everything today'}
                    className="min-w-0 truncate self-center text-[10px] leading-none text-muted-foreground"
                >
                    {focusScopeLabel(focus)}
                </span>

                {/* ⭐ THE COUNT RENDERS WITHOUT `data` (Pete, 2026-09-20: *"History and
                    WMS Orders have no far right counts"*). ⛔⛤ ~~`!loading && data`~~ —
                    the old condition. It was not wrong about the NUMBER, it was wrong
                    about the SLOT: this rail opens with `data === null` and stays there
                    until something asks it to search, so the one state Pete was looking
                    at was the one state with no count at all.
                    ** A CONDITION WRITTEN ABOUT THE VALUE DECIDES THE LAYOUT TOO. THE
                       STATE YOU HAVE NO NUMBER FOR IS THE STATE SOMEBODY IS LOOKING AT. **
                    ⚠️ `rows` is `[]` before the first fetch, so this reads `0` — and
                    here that is honest: the panel below says in words that nothing has
                    been searched yet, so the number agrees with the sentence instead of
                    contradicting it. ⛔ That is the opposite of History, where an
                    unscoped `0` would claim nothing happened. The difference is whether
                    the panel BELOW the count is showing an empty answer or no question. */}
                {/**
                  * ---- ⭐⭐⭐ THREE NUMBERS, NOT ONE (Pete, 2026-09-27) -------------
                  *
                  * > *"The WMS orders panel should show the XX matched XX appointments -
                  * > so 15 orders in the WMS Orders panel matched how many of the total
                  * > appointments (36) for the day."* → **"Three numbers: 36 appts / 12
                  * > matched / 15 orders"**
                  *
                  * ⭐ AND THIS IS WHY THE TITLE IS NOW "Orders". One flex row, ~300px,
                  * carrying name · 🔍 · (scope) · THIS · ↻.
                  *
                  * ---- ⛔⛔ THE MIDDLE NUMBER COUNTS **APPOINTMENTS**, NOT ORDERS -----
                  *
                  * `matchedAppointmentsInRange` is `COUNT(DISTINCT p.AppointmentId)`, and
                  * it is deliberately NOT derivable from the rows on screen. An order can
                  * take several trucks and a truck can satisfy several orders, so
                  * `rows.filter(booked).length` answers a DIFFERENT question and would
                  * quietly answer it in the same slot.
                  *   ** TWO QUANTITIES THAT AGREE ON YOUR TEST DAY ARE STILL TWO
                  *      QUANTITIES. **
                  *
                  * ⚠️ IT IS `null` ON THE REFUSED PATH — when no direction is ticked the
                  * repository returns early without ever running the match, and `null`
                  * says "not asked", which is not `0`. The ratio simply does not draw.
                  *   ** NOT ASKED IS NOT THE SAME ANSWER AS NONE. **
                  *
                  * ⚠️ AND THE ORDER COUNT KEEPS ITS `+`. `capped` means the WMS had more
                  * than the panel will list; dropping the sign to fit two more numbers
                  * would turn a bounded answer into a wrong one.
                  */}
                {/**
                  * ---- ⭐⭐ TWO NUMBERS, NOT THREE (Pete, 2026-09-27, same day) -----
                  *
                  * > *"Lets also remove the /24 out of the orders panel count. Just have
                  * > that info listed in the note below it but we need it to say 32
                  * > appointments matched to an order and 24 total orders listed (some
                  * > orders are on multiple appointments)."*
                  *
                  * ⛔⛔ THE THIRD NUMBER WAS THE ONE THAT COULD NOT BE READ AS A RATIO.
                  * `36 / 12 / 15` invites the eye to read three things of the same kind,
                  * and the first two ARE — appointments, and the appointments that
                  * matched. The third counts ORDERS, a different noun entirely, and an
                  * order can sit on several appointments. So `12 / 15` looked like a
                  * shortfall and was nothing of the sort.
                  *   ** NUMBERS SET SIDE BY SIDE ARE READ AS THE SAME UNIT, WHATEVER
                  *      THE TOOLTIP SAYS. IF THEY ARE NOT, SEPARATE THEM. **
                  * ⭐ The count stays a ratio of like things; the order total moves to
                  * the sentence below, where it has room to say WHY it differs.
                  *
                  * ⚠️ THE SLOT MUST NEVER BE EMPTY. `rows.length` used to render
                  * unconditionally, so something was always there. It no longer does, so
                  * the `apptsInRange === undefined` branch falls back to the order count
                  * — that is the panel mounted outside a board, where "appointments on
                  * the board" is not a number that exists.
                  *   ** A CONDITION WRITTEN ABOUT THE VALUE DECIDES THE LAYOUT TOO. **
                  *
                  * ⚠️ AND THE `+` FOLLOWS THE ORDER COUNT WHEREVER IT GOES. `capped`
                  * means the WMS had more than the panel will list; a bounded answer
                  * printed bare is a wrong one.
                  */}
                {/* ⭐⭐ THE VIEW ICONS, IN THE HEADER (Pete, 2026-09-28): *"This is suppose to
                    match Scanned Paperwork. In the header, not in the body."* Same place as
                    `ScanSourceToggle` there — after the title, before the count and ↻ — and
                    the same group. ⛔ No word beside it; each icon's tooltip names its view.
                    ⚠️ `ml-auto` lives HERE now, so the count and ↻ ride to the right with it. */}
                {viewToggle}
                {!loading && (
                    <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground"
                          title={[
                              apptsInRange === undefined
                                  ? null
                                  : `${apptsInRange} appointment${apptsInRange === 1 ? '' : 's'} on the board (unfiltered, blocks included)`,
                              data?.matchedAppointmentsInRange === null || data?.matchedAppointmentsInRange === undefined
                                  ? null
                                  : `${data.matchedAppointmentsInRange} of them matched to a WMS order`,
                              `${rows.length}${data?.capped ? '+' : ''} order${rows.length === 1 ? '' : 's'} listed — some are on more than one appointment`,
                              data
                                  ? (data.searchedAllDates ? 'Searching every date' : data.scopeLabel)
                                  : 'Nothing searched yet',
                          ].filter(Boolean).join('\n')}>
                        {apptsInRange === undefined ? (
                            <>{rows.length}{data?.capped ? '+' : ''}</>
                        ) : (
                            <>
                                {apptsInRange}
                                {data?.matchedAppointmentsInRange !== null
                                    && data?.matchedAppointmentsInRange !== undefined && (
                                    /* ⭐ THE ONE NUMBER WORTH A COLOUR. It is the answer to
                                       the question Pete asked — how much of today do we
                                       actually have paperwork for — and the other is its
                                       context. ⚠️ `null` is the REFUSED path (no direction
                                       ticked), and the ratio simply does not draw:
                                         ** NOT ASKED IS NOT THE SAME ANSWER AS NONE. ** */
                                    <>
                                        <span className="opacity-60"> / </span>
                                        <span className="font-medium text-emerald-700">{data.matchedAppointmentsInRange}</span>
                                    </>
                                )}
                            </>
                        )}
                    </span>
                )}

                {/* ⭐ REFRESH, FAR RIGHT — Pete: *"A refresh button on the far right
                    to refresh the default search."* ⚠️ Outside the collapse toggle,
                    so it re-reads without folding the panel. */}
                <button
                    type="button"
                    onClick={() => { setOpen(true); void load(); }}
                    disabled={loading}
                    className={`rounded p-0.5 hover:bg-muted disabled:opacity-40 ${loading ? 'ml-auto' : ''}`}
                    title="Re-read the orders now"
                >
                    <RefreshCw className="h-3 w-3" />
                </button>
            </div>

            {open && (
                <div className="min-h-0 overflow-y-auto px-3 py-2">
                    {showFilters && (
                        <div className="mb-2 space-y-1.5">
                            {/**
                              * ⭐ THE SOURCE BOXES (Pete, 2026-09-09): *"WMS panel
                              * should have checkboxes for Camelot and Depsoco to
                              * filter and one for OTher for future WMS additions."*
                              *
                              * ⚠️ OTHER IS NOT A SOURCE NAME — it is "anything that
                              * is not one of the two we know", so a third WMS appears
                              * without a deploy. The server builds it as a NOT IN.
                              */}
                            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                {ALL_SOURCE_KEYS.map(k => (
                                    <label key={k} className="flex cursor-pointer items-center gap-1 text-muted-foreground">
                                        <input type="checkbox" checked={sourceTicked(k)} onChange={() => toggleSource(k)} />
                                        {k === 'OTHER' ? 'Other' : k}
                                    </label>
                                ))}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                {/**
                                  * ⭐ TWO CHECKBOXES, matching the source boxes above.
                                  * ⚠️ Both ticked is NO FILTER (the request omits the
                                  * param); neither ticked shows nothing, honestly.
                                  */}
                                {(['INBOUND', 'OUTBOUND'] as const).map(d => (
                                    <label key={d} className="flex cursor-pointer items-center gap-1 text-muted-foreground">
                                        <input
                                            type="checkbox"
                                            checked={directions === null || directions.has(d)}
                                            onChange={() => setDirections(prev => {
                                                const base = prev ?? new Set<'INBOUND' | 'OUTBOUND'>(['INBOUND', 'OUTBOUND']);
                                                const next = new Set(base);
                                                if (next.has(d)) next.delete(d); else next.add(d);
                                                return next.size === 2 ? null : next;
                                            })}
                                        />
                                        {d === 'INBOUND' ? 'Inbound' : 'Outbound'}
                                    </label>
                                ))}
                            </div>
                            {/* ⛔⤴ ~~Booked {range} / Never booked / Other sites~~ — REMOVED
                                2026-09-28 (B6). D3's Matched / Not matched / All scheduled
                                views do this job above the list, where they cannot be
                                hidden by a collapsed strip. */}

                            {/**
                              * ---- ⭐⭐ THE STATUS BOXES (#359) --------------------
                              *
                              * Pete: *"Think we need to narrow the search on certain
                              * WMS order status's too. That is why we are getting so
                              * many."*
                              *
                              * ⛔ PLAIN WORDS, NOT THE GROUP CODES. A scheduler does
                              * not know what `DONE` covers; "Finished" plus the (i)
                              * listing the actual statuses does the explaining.
                              *
                              * ⛔⛔ AND THERE IS NO "UNRECOGNISED" BOX. A status
                              * nobody has classified is ALWAYS shown — a checkbox
                              * would invite hiding exactly the orders we understand
                              * least, and a truck would go missing off the board.
                              */}
                            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                {STATUS_BOXES.map(({ key, label, hint }) => (
                                    <label key={key} title={hint}
                                           className="flex cursor-pointer items-center gap-1 text-muted-foreground">
                                        <input
                                            type="checkbox"
                                            checked={statusGroups === null ? key === 'OPEN' : statusGroups.has(key)}
                                            onChange={() => setStatusGroups(prev => {
                                                /* ⚠️ THE FIRST CLICK MATERIALISES THE DEFAULT, then
                                                   toggles within it. Starting from an empty set would
                                                   make one click mean "only this", which is not what
                                                   ticking a second box looks like it should do. */
                                                const base = prev ?? new Set<StatusGroupKey>(['OPEN']);
                                                const next = new Set(base);
                                                if (next.has(key)) next.delete(key); else next.add(key);
                                                return next;
                                            })}
                                        />
                                        {label}
                                    </label>
                                ))}
                                {/**
                                  * ⭐ THE (i). Pete: *"lets put the mapping in laman terms
                                  * in an (i) on the WMS Panel so people know what its
                                  * pulling as default"*.
                                  *
                                  * ⛔ §4.0 — NEVER A NATIVE BROWSER DIALOG. It expands in
                                  * place, below, inside the strip.
                                  */}
                                <button type="button" onClick={() => setShowLegend(v => !v)}
                                        aria-expanded={showLegend}
                                        title="What these statuses mean, and what is hidden"
                                        className="ml-auto flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[9px] font-semibold text-muted-foreground hover:bg-muted">
                                    i
                                </button>
                            </div>

                            {showLegend && <StatusLegend data={data} statusGroups={statusGroups} />}

                            {/**
                              * ---- ⭐⭐ THE SEARCH BAR LIVES HERE NOW ---------------
                              *
                              * Pete, 2026-09-09, in three messages: *"there is no open
                              * search box to just search a number?"* → *"actually, i
                              * see it now. needs to be at the top"* → *"The search bar
                              * needs to be part of the checkbox filters and
                              * collaspable."*
                              *
                              * ⛔ IT USED TO SIT BELOW THE LIST. With 200 rows above it
                              * it was off the bottom of a 340px rail and unreachable —
                              * so the panel APPEARED to have no search at all, exactly
                              * when a scheduler needs one. That was my literal reading
                              * of the original *"a search bar below the panel"*.
                              *
                              * ⚠️ IT IS STILL A DIFFERENT THING FROM THE BOXES ABOVE,
                              * and the placeholder has to keep saying so. The filters
                              * narrow the DATED list; this abandons the date window and
                              * searches EVERY date. A scheduler who reads them as one
                              * control types a container number, gets a dated list, and
                              * concludes the order does not exist.
                              */}
                            <form
                                className="flex items-center gap-1 border-t pt-1.5"
                                onSubmit={(e) => { e.preventDefault(); setSubmittedQ(q.trim()); }}
                            >
                                <input
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Filter loaded — Enter searches every date"
                                    className="min-w-0 flex-1 rounded border px-1.5 py-1 text-[11px]"
                                />
                                {/* ⭐ THE SHARED SEARCH ICON, AND THE SHARED SPINNER. Pete,
                                    2026-09-22: *"turn Search to the Icon we use for search too
                                    so its consistent everywhere."* The calendar search box
                                    already swaps Search for a spinning Loader2 while busy;
                                    copying it means these two rails say "working" the same way
                                    rather than each inventing a way to say it.
                                    ⚠️ sr-only text stays - an icon-only control with no
                                    accessible name is a button a screen reader calls "button". */}
                                <button type="submit" title="Search every date"
                                        className="shrink-0 rounded border px-1.5 py-1 text-[11px] hover:bg-muted">
                                    {loading
                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        : <Search className="h-3.5 w-3.5" />}
                                    <span className="sr-only">Search</span>
                                </button>
                                {/* ⛔⛤ ~~Clear~~ - struck 2026-09-22 (§0.3). It reset the box
                                    and the panel; Pete asked for the slot to carry Stop instead,
                                    because clearing a one-line field is two keystrokes and a
                                    running search had no way out.
                                    ⚠️ ONLY WHILE THERE IS SOMETHING TO STOP - a button that
                                    usually does nothing is one nobody trusts when it matters. */}
                                {loading && (
                                    <button type="button"
                                            onClick={cancelSearch}
                                            className="shrink-0 rounded border px-1.5 py-1 text-[11px] text-muted-foreground hover:bg-muted"
                                            title="Stop waiting for this search. The request has already been sent and cannot be recalled; its answer will be discarded.">
                                        Stop
                                    </button>
                                )}
                            </form>
                            {/* ⭐ B5 — while typing, SAY that this is the loaded list, how
                                many match, and that Enter goes to the database. */}
                            {localNeedle && (
                                <div className="mt-1 text-[10px] text-muted-foreground">
                                    {rows.length} of the {viewRows.length} loaded order{viewRows.length === 1 ? '' : 's'} match
                                    {' '}“{localNeedle}”{data?.capped ? ' (the loaded list is capped)' : ''}.
                                    {' '}Press <span className="font-medium">Enter</span> to search every date.
                                </div>
                            )}
                        </div>
                    )}

                    {/* ⛔⤴ ~~THE VIEW CONTROL, HERE IN THE BODY, WITH ITS WORD~~ — MOVED 2026-09-28
                        to the HEADER (Pete: *"This is suppose to match Scanned Paperwork. In the
                        header, not in the body. Lose the 'Matched' next to the icon."*). See
                        `viewToggle` above; the tooltips carry each view's name. */}

                    {/**
                      * ---- ⭐⭐ §1Z.2's FIRST PAYMENT: THE SCOPE SENTENCE --------
                      *
                      * ⛔ ON EVERY RESULT, not only the empty one. *"A short list is
                      * the same lie in miniature"* — five rows with no scope reads as
                      * "there are five", and the sixth is next Tuesday.
                      */}
                    {/**
                      * ⛔⛔ AND IT SITS OUTSIDE `{showFilters && ...}` ON PURPOSE.
                      * Pete: *"The status message ... needs to be below the search bar
                      * and above results and visible when a message is active."* If it
                      * collapsed with the filters, collapsing them would hide the only
                      * sentence explaining why the list is empty — and an unexplained
                      * empty panel reads as "there are no orders".
                      */}
                    {excludedByFilter ? (
                        /**
                         * ⛔⛔ "NOTHING DUE" WOULD BE A FLAT LIE HERE. Plenty is due;
                         * the filter is excluding all of it. Pete read exactly this
                         * state as broken checkboxes — *"Check boxes not working
                         * properly ... not showing any results"* — because the panel
                         * said "Nothing due today." while every source box was ticked
                         * and both direction boxes were not. §1N.2: absence is
                         * reported, never papered over.
                         *
                         * ⭐ AND THE WAY BACK IS OFFERED IN PLACE (§1Z.2), not left
                         * for him to hunt in a strip that may be collapsed.
                         */
                        <div className="mb-1.5 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                            <span>
                                {excludedByFilter === 'direction'
                                    ? 'Every direction is unticked, so nothing can match.'
                                    : 'Every WMS source is unticked, so nothing can match.'}
                                {' '}
                                <button
                                    type="button"
                                    className="underline"
                                    onClick={() => {
                                        setShowFilters(true);
                                        if (excludedByFilter === 'direction') setDirections(null);
                                        else if (excludedByFilter === 'source') setSources(null);
                                    }}
                                >
                                    Undo that filter
                                </button>
                            </span>
                        </div>
                    ) : data && (data.searchedAllDates || boardScopeNote(boardHidden) || data.droppedDefaults.length > 0) && (
                        <div className="mb-1.5 text-[10px] text-muted-foreground">
                            {/**
                              * ---- ⛔⤴ THE STANDING SENTENCE IS GONE (Pete, 2026-09-28) --------
                              *
                              * > *"Lose the wording including the Show All scheduled, first 15
                              * > only. Should show all matched orders period."*
                              *
                              * ~~"Showing the orders on this board. Showing today. N appointments
                              * matched to an order and M+ total orders listed (…). K orders are
                              * not on it — show all scheduled. First M only."~~ — removed. The
                              * header's count and its tooltip carry the numbers; the icons'
                              * tooltips carry the view.
                              *
                              * ⚠️ "First 15 only" WAS WRONG AS WELL AS WORDY. `capped` describes
                              * the whole dated list (over 200 rows), and the sentence printed it
                              * against the VIEW's length — so a complete matched list of 15 read
                              * as the first 15 of more. And the server now returns EVERY matched
                              * order regardless of the cap (ordersPanelRepository.ts), so for the
                              * Matched view there is no cap to report.
                              *
                              * ⭐ WHAT STAYS: the notes that only appear in a special state and
                              * explain a list that would otherwise mislead — a search across every
                              * date, the board's own filters hiding orders, defaults a search
                              * dropped. They draw nothing on an ordinary day.
                              */}
                            {data.searchedAllDates && <>Searching <span className="font-medium">every date</span> for “{submittedQ}”.</>}
                            {boardScopeNote(boardHidden) && <> {boardScopeNote(boardHidden)}</>}
                            {/**
                              * ---- ⛔⛔ §1Z.3 — SAY WHICH DEFAULTS THE SEARCH DROPPED
                              *
                              * > Pete: *"on the search textbox, we need to make sure it
                              * > doesn't limit the criteria the user can't see - so needs
                              * > to be open to a certain extent"*
                              *
                              * ⛔ THE SERVER DROPS THEM; THIS SENTENCE IS THE OTHER HALF.
                              * Without it the boxes on screen stop describing the rows —
                              * "Other sites" sits unticked while other sites' orders are
                              * in the list, which reads as a broken filter.
                              *
                              * ⚠️ IT COMES FROM THE SERVER, NOT FROM RE-DERIVING THE RULE
                              * HERE. A second copy of "what counts as a default" would be
                              * the one that goes stale, and it would go stale by saying
                              * something reassuring.
                              */}
                            {data.droppedDefaults.length > 0 && (
                                <> Ignoring <span className="font-medium">{data.droppedDefaults.join(' and ')}</span> so the search can find it.</>
                            )}
                        </div>
                    )}

                    {/* ⛔ UNREADABLE IS NOT EMPTY, and they must never look alike.
                        ---- ⭐⭐ AND IT NOW CARRIES A WAY OUT (2026-09-24) -----------
                        The server's timeout branch sends a sentence a scheduler can
                        act on ("the search did not finish in time ... an empty result
                        does not mean there is no such order"), so this box no longer
                        has to render "Internal server error" and leave him to guess.
                        ⚠️ THE RETRY IS THE OTHER HALF. Telling somebody a search did
                        not finish, with no way to run it again, is an explanation
                        rather than a remedy — and the calendar has had `Try again`
                        beside the same failure since it was written.
                          ** A MESSAGE THAT NAMES A RECOVERABLE FAILURE OWES THE
                             READER THE CONTROL THAT RECOVERS IT. ** */}
                    {error && (
                        <div className="mb-2 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900">
                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                            <span>
                                The orders could not be read: {error}{' '}
                                <button
                                    type="button"
                                    onClick={() => { void load(); }}
                                    className="underline underline-offset-2 hover:no-underline"
                                >
                                    Try again
                                </button>
                            </span>
                        </div>
                    )}

                    {data?.refusedReason && (
                        <div className="mb-1.5 text-[11px] text-muted-foreground">{data.refusedReason}</div>
                    )}

                    {/* ⭐⭐ THE SCOPE — AND THAT IT ONLY SEES WHAT IS LOADED.
                        ⛔ Pete chose to filter the loaded page rather than re-query, so an order
                        outside the range on screen genuinely cannot appear here. Without this
                        sentence its absence reads as "there is no order for this truck", which is
                        the most expensive wrong answer this panel can give. */}
                    {/* ⛔⛤ ~~THE GREY SCOPE BOX~~ REMOVED 2026-09-19 (§0.3). Pete: *"I don't
                            like the gray box inside the panel and then the same small text line
                            underneath it saying the same thing."*
                            ⚠️ HE WAS DESCRIBING A REAL DUPLICATION, NOT A PREFERENCE: this panel
                            said the date THREE times — "Showing <date>" above, "an order outside
                            <date> would not be here" in the box, and "Nothing due <date>" below.
                            ** A BOX THAT RESTATES ITS NEIGHBOURS IS NOT CONTEXT, IT IS NOISE —
                               AND THE READER STOPS READING ALL THREE. **
                            ✅ The one thing only the box carried — the BLOCKED REASON — moves to a
                            plain line, because it is the sentence that stops a scheduler reading an
                            empty panel as "nothing was sent". ⛔ It must not be lost with the
                            chrome. */}
                        {ordersBlockedReason && (
                            <div className="mb-1.5 text-[11px] text-muted-foreground">{ordersBlockedReason}</div>
                        )}
                    
                    {/**
                      * ---- ⭐⭐ WHY THIS PANEL IS EMPTY, WHEN IT IS THE SCOPE'S FAULT ------
                      *
                      * ⭐ Pete, 2026-09-19, reading a `0` under a `(load)` label: this panel
                      * *"doesn't really pop telling us"* anything, and he took it for a panel
                      * that had failed to update.
                      *
                      * ⛔⛔ THERE WERE **THREE** EMPTY STATES AND ONLY TWO SENTENCES.
                      *   1. nothing loaded at all            -> "Nothing due <range>."
                      *   2. the focus cannot be scoped       -> `ordersBlockedReason`, above
                      *   3. ⛔ **orders ARE loaded, and none of them match this load** — which
                      *      rendered as (1), a sentence about the DAY, under a heading naming
                      *      one load. Indistinguishable from a panel that did not update.
                      *
                      * ⭐ THE THIRD ONE IS THIS LINE. `ordersMatchesFocus` matches on a booked
                      * appointment id OR a normalised PO / reference / container, so a load
                      * carrying numbers that appear on no order reads `0` forever — correctly,
                      * and silently.
                      *   ** AN EMPTY RESULT AND A NON-MATCHING ONE ARE DIFFERENT FACTS. A PANEL
                      *      THAT RENDERS THEM ALIKE IS GUESSING ON THE READER'S BEHALF. **
                      *
                      * ⚠️ GUARDED ON `loadedRows.length > 0`: with nothing loaded there is
                      * nothing to have failed to match, and this sentence would be a second
                      * false explanation beside the true one.
                      */}
                    {!error && rows.length === 0 && !loading && (
                        <div className="rounded border border-dashed px-2 py-2 text-[11px] text-muted-foreground">
                            {/* ⭐ B5 — a local miss is NOT "not found": the loaded list is a
                                dated, capped sample. The way on is offered in place. */}
                            {localNeedle && viewRows.length > 0
                                ? <>
                                    None of the {viewRows.length} loaded order{viewRows.length === 1 ? '' : 's'} match “{localNeedle}”.
                                    {' '}<button type="button" className="underline"
                                                 onClick={() => setSubmittedQ(localNeedle)}>
                                        Search every date
                                    </button>.
                                  </>
                                : focus && !ordersBlockedReason && loadedRows.length > 0
                                ? <>
                                    {loadedRows.length} order{loadedRows.length === 1 ? '' : 's'}{' '}
                                    {data?.scopeLabel ?? 'in this range'}, none matching{' '}
                                    {focusBasisLabel(focus)}.
                                    <div className="mt-1">
                                        Orders match on a booked appointment, or on a shared PO,
                                        reference or container number.
                                    </div>
                                  </>
                                : data?.searchedAllDates
                                    /* ⛔⛤ NAME THE TABLE (2026-09-24). "Nothing matches
                                       that, on any date" was true and useless: it reported
                                       an absence without saying absence FROM WHAT, so it
                                       read as "this number does not exist". It searches
                                       Camelot and Deposco; an appointment reference will
                                       never be here however many dates it covers.
                                         ** AN ABSENCE IS ONLY INFORMATION IF YOU SAY WHAT
                                            IT IS ABSENT FROM. **
                                       ⚠️ SAME SENTENCE AS PoFinder's, deliberately. Two
                                       wordings for one fact is how a scheduler concludes
                                       the two panels disagree. */
                                    ? <>
                                        Nothing matches that in Camelot or Deposco, on any date.
                                        <div className="mt-1">
                                            If it is an appointment reference, use the search box
                                            above the calendar &mdash; that one looks at bookings too.
                                        </div>
                                      </>
                                    : <>Nothing due {data?.scopeLabel ?? 'in this range'}.</>}
                            {/**
                              * ---- ⭐⭐ §1Z.2's THIRD PAYMENT, AND THE POINT OF IT --
                              *
                              * ⛔ *"Nothing found" is what a scheduler mistakes for
                              * "not booked" before turning a driver away from a gate.*
                              * ⚠️ `null` means NOT ASKED and renders nothing — it is
                              * not zero, and saying "0 on other dates" when we never
                              * looked would be the same lie with more confidence.
                              */}
                            {typeof data?.outsideScope === 'number' && data.outsideScope > 0 && (
                                <div className="mt-1 font-medium text-foreground">
                                    {data.outsideScope} on other dates.
                                    {/**
                                      * ⛔ ~~"Use the search BELOW"~~ — WRONG THE MOMENT THE
                                      * SEARCH MOVED. The form went into the collapsible strip
                                      * ABOVE the list on 2026-09-09 and this sentence still
                                      * pointed down at nothing. ⚠️ Exactly the class of defect
                                      * this panel spent the day fixing: a label describing
                                      * something that has moved (§1O.5).
                                      *
                                      * ⭐ SO IT IS A BUTTON, NOT A DIRECTION. §1Z.2 asks for the
                                      * next rung offered IN PLACE — and a control cannot point
                                      * at the wrong part of the screen.
                                      */}
                                    {' '}
                                    <button
                                        type="button"
                                        className="underline"
                                        onClick={() => setShowFilters(true)}
                                        title="Open the search box and look across every date"
                                    >
                                        Search every date
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {rows.map((r) => {
                        const isOpen = expanded.has(r.orderNumber);
                        /* ⭐ A5 — the row is draggable onto an appointment when its key
                           is whole. ⚠️ null means a part of the key is missing, and the
                           server would refuse it - so the drag never starts. */
                        const dragKey = wmsOrderKeyOf(r);
                        return (
                            /**
                              * ---- ⭐⭐ #67 THE ROW UNDER THE CURSOR SAYS SO (Pete, 2026-09-21) --
                              *
                              * > *"the chip is not highlighted as you go over them so you don't
                              * > know what record you are on. It does what I want in Scanned
                              * > Paperwork as an example. Same fix needs to happen for WMS Orders
                              * > and communications."*
                              *
                              * ⭐ THE TREATMENT IS COPIED, NOT INVENTED. `PaperworkTrayCard.tsx`
                              * already uses `hover:border-primary/50`, and its SELECTED state is
                              * `border-primary/60 bg-primary/5`. Borrowing both tones keeps one
                              * vocabulary across the rails instead of a second highlight that
                              * means the same thing in a different colour.
                              *
                              * ⛔⛔ CSS ONLY — NO onMouseEnter HERE, AND THAT IS THE POINT. A rail
                              * row that reports a hover re-scopes the rail, the list rebuilds, the
                              * row under the cursor is replaced, and the browser fires mouseenter
                              * on whatever lands there.
                              *   ** A HOVER THAT RE-SCOPES THE LIST CONTAINING THE HOVERED ROW IS
                              *      A LOOP BY CONSTRUCTION. ** (2026-09-21, fixed at cost.)
                              * ⚠️ `:hover` is the browser's own state. It cannot re-render
                              * anything, so it cannot reopen that loop.
                              */
                            <div key={`${r.wmsSource}:${r.orderType}:${r.orderNumber}`}
                                 /* ⭐⭐ HOVER SCOPES, CLICK LATCHES (Pete, 2026-09-27) — see
                                    `onHoverOrder`. ⛔ ON THE ROW WRAPPER, not on the title
                                    button: the chevron and the title are both buttons inside
                                    it, and putting the gesture on one of them would make the
                                    other a dead zone for scoping.
                                    ⚠️ The CSS `:hover` styling above STAYS. It is what tells
                                    the eye which row the cursor is on, and it cannot re-render
                                    anything — so it survives the loop argument that kept this
                                    row silent until today. */
                                 /* ⛔⛔ AN ORDER ON NO APPOINTMENT SCOPES NOTHING (Pete, 2026-09-28:
                                    *"I clicked on a Match one and now I am stuck"*). Its focus has
                                    no appointment to follow, so it emptied every panel ("Not
                                    matched to an appointment yet"), locked the view icons, and the
                                    row that could have released it was gone from the emptied
                                    list — leaving only Ⓡ, which he had no reason to look for.
                                      ** A SCOPE WITH NOTHING IN IT IS NOT A VIEW. IT IS A TRAP. **
                                    Its job is the Match button and a drag, not the rail. */
                                 onMouseEnter={() => { if (!isUnmatched(r)) reportHover(r); }}
                                 onMouseLeave={() => reportHover(null)}
                                 onClick={() => { if (!isUnmatched(r)) onChooseOrder?.(r.orderNumber, r.bookedOn ?? []); }}
                                 /* ⭐⭐ A5 (Pete, 2026-09-27): *"drag an order to an appointment an
                                    attach it"*. The Day grid's appointment chip is the target
                                    (`GanttCalendar`, `onDropWmsOrderOnAppointment`); the page
                                    shows the replace popup when the order is already elsewhere.
                                    ⛔ The HOVER is released as the drag starts - a rail re-scope
                                    under a drag would unmount this row mid-air (see
                                    `wmsOrderDragInProgress`). */
                                 draggable={dragKey !== null}
                                 onDragStart={(e) => {
                                     if (!dragKey) { e.preventDefault(); return; }
                                     setWmsOrderDrag(e.dataTransfer, dragKey);
                                     e.dataTransfer.setData('text/plain', r.orderNumber);
                                     reportHover(null);
                                 }}
                                 title={dragKey ? 'Drag onto an appointment to attach this order' : undefined}
                                 className={`mb-1 rounded border bg-muted/30 px-2 py-1.5 text-xs
                                            transition-colors hover:border-primary/50 hover:bg-primary/5${dragKey ? ' cursor-grab active:cursor-grabbing' : ''}`}>
                                <div className="flex items-start gap-1">
                                    <button
                                        type="button"
                                        onClick={() => setExpanded(prev => {
                                            const n = new Set(prev);
                                            if (n.has(r.orderNumber)) n.delete(r.orderNumber); else n.add(r.orderNumber);
                                            return n;
                                        })}
                                        aria-expanded={isOpen}
                                        title={isOpen ? 'Less' : 'More detail'}
                                        className="-ml-1 rounded p-0.5 text-muted-foreground hover:bg-muted"
                                    >
                                        {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                    </button>
                                    {/* ⭐ THE WHOLE LINE OPENS THE ORDER. Pete: *"you
                                        should be able to click on the line and a popup
                                        with the order header and order detail show."* */}
                                    <button
                                        type="button"
                                        /* ⛔⛔ ~~onClick={() => setDetailFor(r)}~~ — MOVED TO
                                           DOUBLE CLICK 2026-09-27. Pete: *"Hover causes
                                           everything to scope to that order, appointment,
                                           paperwork, etc. single click does the same thing but
                                           it stays even after the hover … Doubleclick on the
                                           order, opens the order detail just like it does
                                           now."*
                                           ⭐ SINGLE CLICK IS THE ROW'S NOW, so this button must
                                           not swallow it — it does not `stopPropagation`, and
                                           the row's handler runs as well.
                                           ⚠️ NO DEBOUNCE. A double click fires the single click
                                           twice first, which latches the same focus twice — a
                                           no-op — so the two compose and nothing needs delaying.
                                             ** TWO GESTURES THAT COMPOSE NEED NO TIMER; TWO
                                                THAT CONFLICT NEED ONE. ** */
                                        onDoubleClick={() => setDetailFor(r)}
                                        title="Double-click to open this order"
                                        className="min-w-0 flex-1 text-left"
                                    >
                                        <div className="flex items-baseline gap-1.5">
                                            {/**
                                              * ---- ⭐⭐ THE IN / OUT PILL (Pete, 2026-09-09) --------
                                              *
                                              * > *"And should be marked on the individual item with
                                              * > icon like the other things we use in the app"*
                                              *
                                              * ⭐ THE SAME PILL THE CALENDAR CHIP USES — `IN` on blue,
                                              * `OUT` on orange, from `GanttCalendar`. ⛔ Not a new
                                              * mark: one truck appears as a chip on the grid, a card
                                              * in the rail and a row here, and a scheduler should not
                                              * have to learn three vocabularies for one fact.
                                              *
                                              * ⚠️ ABSENT WHEN THE DIRECTION IS UNKNOWN, never guessed.
                                              * `directionOfOrderType` returns null for a type in
                                              * neither list, and painting one blue would put an
                                              * adjustment on a dock door.
                                              */}
                                            {/**
                                              * ---- ⭐⭐ D5 — THE ORDER NUMBER IS FIRST, AND THAT IS
                                              * WHAT MAKES THE COLUMN LINE UP (Pete, 2026-09-27) ----
                                              *
                                              * > *"The Order numbers need to line up far left."*
                                              *
                                              * ⛔ THE PILL WAS THE MISALIGNMENT, not the padding. It
                                              * used to sit here and it is THREE DIFFERENT WIDTHS —
                                              * `IN`, `OUT`, or nothing at all on a type with no
                                              * direction. So every order number started at one of
                                              * three x-positions and the eye could not run down the
                                              * list. Moving it (D4) is what fixes this; there is no
                                              * alignment rule to add.
                                              *   ** A LEADING ELEMENT OF VARIABLE WIDTH IS A RAGGED
                                              *      LEFT EDGE FOR EVERYTHING BEHIND IT. **
                                              * ⚠️ The chevron still precedes the whole block, but it
                                              * is OUTSIDE this button and one fixed size, so it
                                              * indents every row equally — which is alignment, not
                                              * a break in it.
                                              */}
                                            <span className="truncate font-medium">{r.orderNumber}</span>
                                            {r.wmsSource && (
                                                <span className="shrink-0 rounded bg-slate-100 px-1 text-[9px] uppercase tracking-wide text-slate-700">
                                                    {r.wmsSource}
                                                </span>
                                            )}
                                            {/**
                                              * ---- ⭐ THE STATUS SITS BESIDE THE WMS NAME
                                              *
                                              * > Pete, 2026-09-09: *"Move the status from
                                              * > the bottom line to the right of the WMS
                                              * > name on the first line"*
                                              *
                                              * ⭐ IT BELONGS NEXT TO THE SYSTEM THAT
                                              * ISSUED IT. `Printed` means one thing in
                                              * Camelot and nothing in Deposco; read on its
                                              * own line it was a word with no owner.
                                              *
                                              * ⛔ AND IT LEAVES THE LINE BELOW FREE for the
                                              * identifier a driver might actually be
                                              * carrying — which is the whole point of the
                                              * move.
                                              */}
                                            {r.status && (
                                                <span className="shrink-0 truncate text-[10px] text-muted-foreground">
                                                    {r.status}
                                                </span>
                                            )}
                                            {/**
                                              * ---- ⭐⭐⭐ WHY A `Complete` ORDER IS UNDER **Open** (#367)
                                              *
                                              * > Pete, 2026-09-27, with Open ticked and Finished clear:
                                              * > *"The Open, Finished, cancelled should have all the
                                              * > status's showing in the result now, Think its missing
                                              * > complete, entered, new, received"*
                                              *
                                              * ⭐ MEASURED, AND THE FILTER WAS RIGHT. Migration 177 ran on
                                              * 2026-09-09, the mapping table holds 29 rows, and all four of
                                              * those statuses ARE classified. What Pete was seeing is #367:
                                              * **an outbound that has not shipped is OPEN whatever its
                                              * status says.** Deposco's lifecycle puts *Picking* and
                                              * *Packing/shipping* BOTH at `Complete`, separated only by
                                              * `ShippingStatus` — 714 sales orders were being hidden by
                                              * trusting the status word alone.
                                              *
                                              * ⛔⛔ THE RULE WAS INVISIBLE, SO A CORRECT FILTER READ AS A
                                              * BROKEN ONE. That is what this chip fixes — not the query.
                                              *   ** A RULE THE USER CANNOT SEE APPLIED IS A RULE THEY WILL
                                              *      REPORT AS A BUG, AND THEY WILL BE RIGHT TO. **
                                              *
                                              * ⚠️ AMBER, NOT RED. This is normal, expected work in flight —
                                              * red is the no-show and the overdue, and a list full of red
                                              * that means "still in the building" teaches people to ignore
                                              * red (SPEC §7.2, the same argument as the NO WMS mark).
                                              *
                                              * ⛔ `=== true`, NOT TRUTHINESS. The field is `boolean | null`
                                              * and null means "we could not read the shipping code table"
                                              * (§3B.8) or "inbound, not applicable". Absent must render
                                              * nothing rather than quietly claiming shipped.
                                              */}
                                            {r.notShipped === true && (
                                                <span
                                                    className="shrink-0 rounded bg-amber-100 px-1 text-[9px] font-medium text-amber-900"
                                                    title="Outbound and not yet shipped - it counts as open work whatever the WMS status says (#367)"
                                                >
                                                    not shipped
                                                </span>
                                            )}
                                            <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                                                {ymd(r.effectiveDate)}
                                            </span>
                                        </div>
                                        {/**
                                          * ---- ⭐⭐⭐ LINE TWO: WHO, WHAT THEY'RE CARRYING, AND
                                          * WHICH WAY (Pete, 2026-09-27, D4 + D6) ---------------
                                          *
                                          * > *"Any Ref numbers or PO, SO, Container, etc that has
                                          * > value needs to go after the Carrier/Client name on line
                                          * > two separated by a dash."*
                                          * > *"The Out and IN buttons need to go under the date field
                                          * > on far right, 2nd row."*
                                          *
                                          * ⭐ `ml-auto` IS WHAT PUTS THE PILL UNDER THE DATE, because
                                          * the date on line one is also `ml-auto` in a row of the same
                                          * width. ⛔ Not a fixed width or a grid column: the two lines
                                          * carry different content and any hard number would drift the
                                          * first time either changes.
                                          *   ** TWO THINGS ALIGN BECAUSE THEY OBEY THE SAME RULE, NOT
                                          *      BECAUSE THEY WERE GIVEN THE SAME NUMBER. **
                                          *
                                          * ⚠️ `items-center`, NOT `items-baseline`. The pill is a
                                          * bordered box with its own line-height; on a baseline row it
                                          * hangs low against 11px text. Line one keeps `items-baseline`
                                          * because everything on it is text.
                                          *
                                          * ⛔ THE IDENTIFIERS ARE INSIDE THE TRUNCATING SPAN, on
                                          * purpose. They are the part that may be long, and they are
                                          * ordered most-likely-carried first (see `rowIdentifiers`), so
                                          * a narrow rail drops the PRO before it drops the client name.
                                          */}
                                        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                            <span className="min-w-0 truncate">
                                                {r.businessUnit ?? '—'}
                                                {r.customer ? ` · ${r.customer}` : ''}
                                                {(() => {
                                                    const ids = rowIdentifiers(r);
                                                    return ids.length > 0 ? ` - ${ids.join(' - ')}` : '';
                                                })()}
                                            </span>
                                            <span className="ml-auto shrink-0">
                                                <DirectionPill direction={r.direction} orderType={r.orderType} />
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-1 text-[10px] text-muted-foreground">
                                            {/* ⭐ ~~{r.status}~~ MOVED UP to sit beside the
                                                WMS badge (Pete, 2026-09-09). This line is
                                                now the identifier line. */}
                                            {/**
                                              * ---- ⭐⭐ THE REFERENCE NUMBER, ON THE ROW
                                              *
                                              * > Pete, 2026-09-09: *"put the Reference
                                              * > number in the Summary panel for Camelot
                                              * > orders in WMS Orders"*
                                              *
                                              * ⛔⛔ IT IS THE ONLY THING A DRIVER MIGHT
                                              * BE CARRYING. The reference travels client
                                              * → broker → driver, and Camelot outbound
                                              * drivers have a PO or reference only
                                              * **25% of the time** (§1AG). When one does
                                              * turn up at the gate holding a number,
                                              * this is the number — so it belongs where
                                              * a scheduler can scan a list for it, not
                                              * one click down.
                                              *
                                              * ⚠️ SHOWN FOR EVERY SOURCE, NOT ONLY
                                              * CAMELOT, and deliberately. Pete asked for
                                              * Camelot because that is where it is
                                              * load-bearing, but 699 of 773 Deposco
                                              * orders measured on 2026-09-09 carried one
                                              * too. ⛔ A source-conditional row is also a
                                              * hard-coded WMS name in a component, which
                                              * is what the (i) guard forbids for order
                                              * types and for the same reason.
                                              *
                                              * ⭐ LABELLED. A bare string beside a status
                                              * word is exactly how `Printed 200` came to
                                              * read as a quantity (§1O.5).
                                              */}
                                            {/* ⭐ ~~the `Ref` chip~~ MOVED TO LINE TWO 2026-09-27 (D6),
                                                where it now sits with the PO, container, BOL and PRO
                                                after the client name, dash-separated — Pete wanted the
                                                identifiers together rather than one of them promoted to
                                                a chip and the rest hidden behind the chevron. It kept
                                                its label; see `rowIdentifiers`. This line is now the
                                                BOOKING line (D7). */}
                                            {/**
                                              * ---- ⭐⭐ ALREADY ON AN APPOINTMENT ----
                                              *
                                              * > Pete: *"It should also identify if the
                                              * > item is already on an appointment and
                                              * > show that somehow"*
                                              *
                                              * ⛔ THE COUNT, NOT A TICK. An order can
                                              * take several trucks — *"an order can
                                              * obvioulsy have multiple trucks to fulfill
                                              * it but only 1 truck per appointment"* — so
                                              * a bare "booked" would read as finished on
                                              * a load that is one truck into three.
                                              */}
                                            {r.bookedCount > 0 && (
                                                <span className="rounded bg-emerald-100 px-1 font-medium text-emerald-900">
                                                    {/* ⭐ THE DOOR AND THE TIME RIDE
                                                        ALONG (Pete, 2026-09-27) — see
                                                        `bookedWhere`. ⛔ ONE TRUCK ONLY:
                                                        on two or more there is no single
                                                        door or time to name, and picking
                                                        the first would be a guess wearing
                                                        the authority of a fact. The
                                                        dialog lists them all. */}
                                                    {r.bookedCount === 1
                                                        ? `On ${r.bookedOn[0]?.appointmentNumber ?? 'an appointment'}${bookedWhere(r.bookedOn[0])}`
                                                        : `On ${r.bookedCount} appointments`}
                                                </span>
                                            )}
                                            {/**
                                              * ---- ⛔⛔ THE SITE, IN WORDS ----------
                                              *
                                              * ⚠️ THIS CHIP USED TO PRINT THE BARE CODE
                                              * AND IT WAS UNREADABLE. Beside the status
                                              * it rendered as `Printed 200` and parsed
                                              * as a QUANTITY — two hundred somethings
                                              * printed. `200` is Denver. It was on all
                                              * 200+ rows of a list Pete believed was
                                              * Brighton's, and it was the one fact that
                                              * would have told him otherwise.
                                              *
                                              * ⭐ SO IT SAYS "at Denver". The word "at"
                                              * earns its place: it is what stops a name
                                              * that happens to be numeric reading as a
                                              * number again.
                                              */}
                                            {r.elsewhere && (r.facilityName || r.facility) && (
                                                <span className="rounded bg-amber-100 px-1 text-amber-900"
                                                      title="The WMS has this order under another site">
                                                    at {r.facilityName || r.facility}
                                                </span>
                                            )}
                                            {/**
                                              * ⭐ REASSIGNED BY A RULE, NOT BY THE WMS
                                              * (migration 176). ⚠️ A DIFFERENT FACT from
                                              * "this site's order" and labelled
                                              * separately (§1R.17): TRAFFIX and HELLMAN
                                              * are Brighton-only TODAY, and the day one
                                              * of them also ships from Denver an
                                              * unlabelled rule would misfile Denver's
                                              * freight with nothing on screen to say why.
                                              */}
                                            {r.byOverride && (
                                                <span className="rounded bg-sky-100 px-1 text-sky-900"
                                                      title="The WMS filed this under another site; a facility override rule assigns it here">
                                                    by rule
                                                </span>
                                            )}
                                            {/**
                                              * ⚠️ THE DATE IS A SYNC TIMESTAMP, NOT A
                                              * PLAN. The WMS gave this order no planned
                                              * ship or arrival date, so the effective
                                              * date fell through to CreatedDateTime.
                                              * ⛔ Showing that silently presents "when we
                                              * heard about it" as "when it is due"
                                              * (§1N.2).
                                              */}
                                            {r.dateIsFallback && (
                                                <span className="rounded bg-muted px-1"
                                                      title="No planned ship or arrival date in the WMS - this is when the order was created">
                                                    no planned date
                                                </span>
                                            )}
                                        </div>
                                    </button>
                                </div>
                                {/* ⭐ D3 / Decision 238 — Match, on an order with no appointment.
                                    ⛔ stopPropagation: the row's own click latches a focus.
                                    ⭐⭐ ITS OWN LINE, RIGHT-ALIGNED (Pete, 2026-09-28: *"The IN and OUT
                                    should always lineup on the rightside"*). Beside the card it took
                                    ~45px off the card's width on exactly the rows that have it, so
                                    their date and IN / OUT pill sat left of every other row's.
                                      ** A CONTROL THAT ONLY SOME ROWS HAVE MUST NOT SHARE A ROW
                                         WITH THE COLUMN EVERY ROW HAS. ** */}
                                {onMatchOrder && dragKey && isUnmatched(r) && (
                                    <div className="mt-1 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); reportHover(null); onMatchOrder(dragKey, r); }}
                                            onDoubleClick={(e) => e.stopPropagation()}
                                            title="Attach this order to one of the day's appointments"
                                            className="shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10"
                                        >
                                            Match
                                        </button>
                                    </div>
                                )}

                                {isOpen && (
                                    <div className="mt-1 space-y-0.5 border-t pt-1 text-[10px] text-muted-foreground">
                                        {r.customerOrderNumber && <div>Customer PO · {r.customerOrderNumber}</div>}
                                        {r.customerReference && <div>Reference · {r.customerReference}</div>}
                                        {r.containerNumber && <div>Container · {r.containerNumber}</div>}
                                        {r.bolNumber && <div>BOL · {r.bolNumber}</div>}
                                        {r.proNumber && <div>PRO · {r.proNumber}</div>}
                                        <div>
                                            {r.orderType ?? '—'}
                                            {r.direction ? ` · ${r.direction === 'INBOUND' ? 'Inbound' : 'Outbound'}` : ''}
                                        </div>
                                        {r.bookedOn.length > 0 && (
                                            <div>Booked on {r.bookedOn.map(b => b.appointmentNumber ?? b.appointmentId).join(', ')}</div>
                                        )}
                                        {/* ⚠️ A CACHE ROW CAN BE STALE AND SAYS SO.
                                            Pipeline syncs this table; nothing here calls
                                            a WMS live, and a screen implying otherwise
                                            is a lie the first time a sync stalls. */}
                                        {r.rowUpdatedAt && <div>Synced {ymd(r.rowUpdatedAt)}</div>}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* ⭐ ~~THE SEARCH BAR USED TO BE HERE, BELOW THE LIST.~~ MOVED
                        2026-09-09 into the collapsible filter strip at the top (Pete:
                        *"The search bar needs to be part of the checkbox filters and
                        collaspable"*). ⛔ Below 200 rows in a 340px rail it was off the
                        bottom and unreachable, so the panel appeared to have no search
                        at all. The note stays where the mistake was (§0.3). */}
                </div>
            )}

            <OrderDetailDialog
                row={detailFor}
                getToken={getToken}
                facilityCode={facilityCode}
                onClose={() => setDetailFor(null)}
                onCreateFromOrder={onCreateFromOrder}
            />
        </div>
    );
}

/**
 * ---- ⭐⭐ THE ORDER POPUP — HEADER AND LINES -------------------------------
 *
 * > Pete, 2026-09-09: *"you should be able to click on the line and a popup with
 * > the order header and order detail show. maybe a button to create new
 * > appointment or add to existing appointment"*
 *
 * ⭐ IT REUSES `po-detail`, WHICH ALREADY EXISTS. The PO picker inside the
 * appointment dialog reads the same endpoint for the same rows; a second
 * detail query would be a second answer to "what is on this order".
 *
 * ⚠️ "ADD TO EXISTING APPOINTMENT" IS NOT HERE YET, deliberately. Pete scoped
 * this build to *"Panel only — list, filters, search, refresh"*, and that action
 * belongs with the drag work (#335) which also has to decide what happens to an
 * appointment's existing order data. ⛔ A button that opened a picker and then
 * could not finish would be worse than its absence.
 */
function OrderDetailDialog({ row, getToken, facilityCode, onClose, onCreateFromOrder }: {
    row: OrdersPanelRowDto | null;
    getToken: () => Promise<string>;
    facilityCode: string;
    onClose: () => void;
    onCreateFromOrder?: (row: OrdersPanelRowDto) => void;
}) {
    const [detail, setDetail] = useState<PoDetailResult | null>(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    /** ⭐ F80: bumping this re-reads the order (the lines' Retry button). */
    const [readNo, setReadNo] = useState(0);

    useEffect(() => {
        if (!row) { setDetail(null); setErr(null); return; }
        let live = true;
        setLoading(true); setErr(null);
        (async () => {
            try {
                /* ⛔ #342 - facilityCode scopes the read to this company. */
                const d = await poDetail(await getToken(), row.orderNumber,
                                         row.wmsSource ?? '', row.orderType ?? '', facilityCode);
                if (live) setDetail(d);
            } catch (e) {
                if (live) setErr(e instanceof Error ? e.message : String(e));
            } finally {
                if (live) setLoading(false);
            }
        })();
        return () => { live = false; };
    }, [row, getToken, facilityCode, readNo]);

    const lines = detail?.lines ?? [];
    /** ⭐ F80: the header arrived, the lines did not. ⛔ NOT "no lines". */
    const linesTimedOut = detail?.linesError === 'timeout';
    const total = useMemo(() => lines.length, [lines]);

    return (
        <Dialog open={row !== null} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    {/* ⭐ DIRECTION FIRST, then the number — the same order the row uses,
                        so the eye finds IN/OUT in the same place whichever it is reading.
                        ⚠️ `items-center`, because the pill is shorter than the title text
                        and a baseline row would hang it low. */}
                    <DialogTitle className="flex items-center gap-2">
                        <DirectionPill direction={row?.direction} orderType={row?.orderType} />
                        {row?.orderNumber}
                        {row?.wmsSource && <span className="text-sm font-normal text-muted-foreground">{row.wmsSource}</span>}
                    </DialogTitle>
                </DialogHeader>

                {row && (
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
                        <Fact label="Type" value={row.orderType} />
                        <Fact label="Client" value={row.businessUnit} />
                        <Fact label="Customer" value={row.customer} />
                        <Fact label="Status" value={row.status} />
                        {/* ⭐ F60 — OUTBOUND ONLY: has it left the building? Deposco's status
                            stays "Complete" after shipping; its Shipping Status says it. An
                            older backend sends no label, so fall back to the chip's boolean. */}
                        {row.direction === 'OUTBOUND' && (
                            <Fact
                                label="Shipping"
                                value={row.shippingStatus
                                    ?? (row.notShipped === false ? 'Shipped' : row.notShipped ? 'Not shipped' : null)}
                            />
                        )}
                        <Fact label="Planned arrival" value={ymd(row.plannedArrivalDate)} />
                        <Fact label="Planned ship" value={ymd(row.plannedShipDate)} />
                        <Fact label="Customer PO" value={row.customerOrderNumber} />
                        <Fact label="Reference" value={row.customerReference} />
                        <Fact label="Container" value={row.containerNumber} />
                        <Fact label="BOL" value={row.bolNumber} />
                        <Fact label="PRO" value={row.proNumber} />
                        {/**
                          * ---- ⭐⭐⭐ WHEN THIS ROW LAST CAME FROM THE WMS ----------
                          *
                          * > Pete, 2026-09-27: *"having a recorded updated field on the
                          * > detail would be key so we know when the orders was pulled last
                          * > from the sync - or maybe a order refreshed date/time."*
                          *
                          * ⭐ IT NEEDED NO BACKEND WORK. `rowUpdatedAt` has been on this DTO
                          * all along — it is what the search's *"synced 9d ago"* is computed
                          * from — and this dialog already receives the whole row. The fact was
                          * being fetched, carried and thrown away.
                          *   ** BEFORE ADDING A FIELD, ASK WHETHER THE ONE YOU WANT IS
                          *      ALREADY ARRIVING AND SIMPLY NOT RENDERED. **
                          *
                          * ⛔⛔ THE ABSOLUTE TIME IS THE ANSWER; THE AGE IS THE NUDGE.
                          * `staleness` returns null under two hours — right for a list, where
                          * the string only earns its space when something is wrong — but a
                          * detail pane asked "when was this pulled" must always say. So the
                          * timestamp always renders and the age rides beside it when there is
                          * one.
                          *   ** "IS THIS WORTH MENTIONING" AND "WHEN WAS IT" ARE DIFFERENT
                          *      QUESTIONS, AND A HELPER THAT ANSWERS THE FIRST MUST NOT BE
                          *      USED TO ANSWER THE SECOND. **
                          *
                          * ⚠️ IT IS THE **CACHE ROW'S** TIMESTAMP, NOT THE ORDER'S. It says
                          * when we last heard from Camelot or Deposco about this order — not
                          * when anybody changed it there. A row synced two minutes ago can
                          * still be describing something that moved yesterday, so the label
                          * says "Synced from WMS" rather than "Updated".
                          *   ** A DATE IS ONLY USEFUL WITH THE EVENT IT MARKS NAMED BESIDE
                          *      IT. **
                          */}
                        <Fact
                            label="Synced from WMS"
                            wrap
                            value={row.rowUpdatedAt
                                ? `${new Date(row.rowUpdatedAt).toLocaleString()}${
                                    staleness(row.rowUpdatedAt) ? ` · ${staleness(row.rowUpdatedAt)}` : ''}`
                                : null}
                        />
                        {/**
                          * ⚠️ THE NAME, NOT THE BARE CODE — §1O.5, and the row chip was
                          * fixed first while this one was missed. `200` beside a label
                          * reads as a quantity or an id; "Denver (200)" reads as a place.
                          * ⛔ The code is kept in brackets because it IS what the WMS
                          * wrote, and a scheduler comparing this dialog with Camelot
                          * needs the value Camelot uses.
                          */}
                        <Fact label="WMS facility"
                              value={row.facilityName && row.facilityName !== row.facility
                                  ? `${row.facilityName} (${row.facility})`
                                  : row.facility} />
                    </div>
                )}

                {/* ⭐ ALREADY BOOKED, NAMED. The count is the fact; the numbers are
                    what a scheduler needs to go and look. */}
                {row && row.bookedCount > 0 && (
                    <div className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900">
                        Already on {row.bookedCount === 1 ? 'appointment' : `${row.bookedCount} appointments`}
                        {': '}
                        {row.bookedOn.map(b => b.appointmentNumber ?? b.appointmentId).join(', ')}
                        {row.bookedCount > row.bookedOn.length && ' …'}
                    </div>
                )}

                <div className="min-h-0">
                    <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Lines {total > 0 && <span className="font-normal">({total})</span>}
                    </div>
                    {loading && <div className="text-xs text-muted-foreground">Reading the order…</div>}
                    {/* ⛔ UNREADABLE IS NOT EMPTY. */}
                    {err && <div className="text-xs text-rose-700">The order detail could not be read: {err}</div>}
                    {!loading && !err && linesTimedOut && (
                        <div className="flex items-center gap-2 text-xs text-amber-700">
                            <span>The line items took too long to load — the database was busy.</span>
                            <button
                                type="button"
                                className="rounded border border-amber-400 px-1.5 py-0.5 font-medium hover:bg-amber-50"
                                onClick={() => setReadNo((n) => n + 1)}
                            >
                                Retry
                            </button>
                        </div>
                    )}
                    {!loading && !err && !linesTimedOut && lines.length === 0 && (
                        <div className="text-xs text-muted-foreground">No lines on this order.</div>
                    )}
                    {lines.length > 0 && (
                        /* ⚠️ ITS OWN SCROLLER, so a 200-line order cannot push the
                           buttons off the bottom of the dialog. */
                        <div className="max-h-64 overflow-y-auto rounded border">
                            <table className="w-full text-left text-[11px]">
                                <thead className="sticky top-0 bg-muted/60">
                                    {/**
                                      * ⚠️ THESE COLUMN NAMES CAME FROM `PoDetailLine`,
                                      * not from what a line "should" have. My first
                                      * draft invented `description` and `quantity`;
                                      * neither exists, and only `tsc` with the real
                                      * client wired in said so. §1R.17 — a column
                                      * heading is a claim about every cell beneath it.
                                      */}
                                    <tr>
                                        <th className="px-1.5 py-1 font-medium">Line</th>
                                        <th className="px-1.5 py-1 font-medium">Item</th>
                                        <th className="px-1.5 py-1 text-right font-medium">Ordered</th>
                                        <th className="px-1.5 py-1 text-right font-medium">Shipped</th>
                                        <th className="px-1.5 py-1 font-medium">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lines.map((l, i) => (
                                        <tr key={i} className="border-t">
                                            <td className="px-1.5 py-1 tabular-nums">{l.lineNumber ?? i + 1}</td>
                                            <td className="px-1.5 py-1">{l.itemNumber ?? '—'}</td>
                                            <td className="px-1.5 py-1 text-right tabular-nums">
                                                {l.orderedQty ?? '—'}{l.uom ? ` ${l.uom}` : ''}
                                            </td>
                                            {/* ⚠️ SHOWN EVEN AT ZERO. A blank would read as
                                                "not loaded", and the gap between ordered and
                                                shipped is the whole question.
                                                ⛔⛤ ~~"Received"~~ — the column was labelled
                                                that until 2026-09-27 while holding
                                                `ShippedQuantity`, which on an OUTBOUND order
                                                said a truck had received its own load. */}
                                            <td className="px-1.5 py-1 text-right tabular-nums">{l.shippedQty ?? '—'}</td>
                                            <td className="px-1.5 py-1">{l.lineStatus ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <button type="button" onClick={onClose}
                            className="rounded border px-3 py-1.5 text-sm">
                        Close
                    </button>
                    {onCreateFromOrder && row && (
                        <button
                            type="button"
                            onClick={() => { onCreateFromOrder(row); onClose(); }}
                            className="rounded bg-foreground px-3 py-1.5 text-sm text-background"
                        >
                            New appointment
                        </button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/**
 * ============================================================================
 * ⭐⭐ THE (i) — WHAT THE PANEL IS FILTERING, READ FROM THE FILTER'S OWN TABLE
 * ============================================================================
 *
 * > Pete, 2026-09-09: *"lets put the mapping in laman terms in an (i) on the WMS
 * > Panel so people know what its pulling as default"*
 *
 * ---- ⛔⛔ NOT ONE WORD OF THIS LIST IS WRITTEN IN THIS FILE ---------------
 *
 * Every status name comes from `data.statusLegend`, which the server reads from
 * `dock.WmsOrderStatusGroup` in the SAME request that applies the filter. ⚠️ The
 * tempting version is a nice paragraph right here — and it would be a SECOND
 * source of truth, of which only the other one is enforced. The moment Camelot
 * answers about `Posted` and somebody runs the UPDATE, the paragraph starts
 * lying, renders perfectly, and nothing anywhere reports it.
 *
 * ⛔ AND IT SAYS WHAT IS **HIDDEN**, NOT ONLY WHAT IS SHOWN. A filter explaining
 * only its inclusions is how somebody concludes an order does not exist.
 */
function StatusLegend({ data, statusGroups }: {
    data: OrdersPanelDto | null;
    statusGroups: Set<StatusGroupKey> | null;
}) {
    const [showSql, setShowSql] = useState(false);
    const shown = statusGroups === null ? new Set<string>(['OPEN']) : new Set<string>(statusGroups);
    const c = data?.criteria ?? null;
    /* ⭐ WHICH SOURCES HAVE ANY CLASSIFICATION AT ALL, read from the legend the
       server sent. ⚠️ Derived, never hard-coded: the day Camelot is classified
       this line changes on its own rather than needing an edit nobody makes. */
    const sourcesWithLegend = [...new Set((data?.statusLegend ?? []).map(r => r.wmsSource))].sort();

    /* ⚠️ null MEANS THE LOOKUP FAILED, which is not "nothing is classified".
       Saying we cannot explain the filter beats implying nothing is filtered. */
    if (!data?.statusLegend) {
        return (
            <div className="rounded border bg-muted/40 px-2 py-1.5 text-[10px] text-muted-foreground">
                The status list could not be read just now, so this panel cannot explain what
                it is filtering. The list above is still filtered.
            </div>
        );
    }

    const byGroup = new Map<string, string[]>();
    for (const r of data.statusLegend) {
        const k = r.groupCode;
        if (!byGroup.has(k)) byGroup.set(k, []);
        byGroup.get(k)!.push(`${r.currentStatus} (${r.wmsSource})`);
    }

    return (
        <div className="space-y-1 rounded border bg-muted/40 px-2 py-1.5 text-[10px] text-muted-foreground">
            <div className="font-medium text-foreground">What this panel is showing</div>

            {/**
              * ---- ⭐⭐ WHAT IT PULLED, IN PLAIN WORDS (#366) -------------------
              *
              * > Pete, 2026-09-09: *"I want the queries shown in the (i) for the
              * > WMS Panel so we know what we are pulling and how it correlates
              * > from our DB to Deposco."*
              *
              * ⚠️ EVERY VALUE HERE COMES FROM `data.criteria`, which the server
              * built from the SAME constants the query used. ⛔ Nothing on this
              * screen is typed out by hand — a paragraph describing the rules
              * would drift the first time one changed, and it would drift
              * silently because a wrong explanation still renders.
              */}
            {c && c.directions.length > 0 && (
                <div className="border-t pt-1">
                    {c.directions.map(d => (
                        <div key={d.direction}>
                            <span className="font-medium text-foreground">
                                {d.direction === 'INBOUND' ? 'Inbound' : 'Outbound'}
                            </span>{' '}
                            = {d.orderTypes.join(', ')} — dated on{' '}
                            {d.direction === 'INBOUND'
                                ? 'planned arrival, then planned ship, then created'
                                : 'planned ship, then actual ship, then created'}.
                        </div>
                    ))}
                    <div>Scope: <span className="font-medium text-foreground">{c.facilityScope}</span>.</div>
                    <div>
                        A typed search matches {c.searchColumns.length} fields —{' '}
                        {c.searchColumns.map(x => x.label).join(' · ')}.{' '}
                        {c.searchColumns.filter(x => x.separatorInsensitive).length} of them ignore
                        punctuation, so ABCU-123456 7 finds ABCU1234567.
                    </div>
                </div>
            )}

            {STATUS_BOXES.map(({ key, label }) => (
                <div key={key}>
                    <span className={shown.has(key) ? 'font-medium text-foreground' : ''}>
                        {shown.has(key) ? 'Showing' : 'Hidden'} — {label}:
                    </span>{' '}
                    {(byGroup.get(key) ?? ['(nothing classified yet)']).join(', ')}
                </div>
            ))}
            {/**
              * ⛔⛔ THE PARAGRAPH THAT MATTERS MOST. An unrecognised status is
              * ALWAYS shown, and a scheduler seeing an odd row needs to know that
              * is deliberate rather than assume the panel is confused.
              */}
            <div className="border-t pt-1">
                <span className="font-medium text-foreground">Always shown — anything not in that list.</span>{' '}
                A status we have not classified yet is never hidden, because hiding it could
                hide a truck.
                {data.unclassifiedSeen.length > 0 && (
                    <> On this screen: <span className="font-medium text-foreground">
                        {data.unclassifiedSeen.map(u => `${u.currentStatus} (${u.wmsSource})`).join(', ')}
                    </span>.</>
                )}
            </div>

            {/**
              * ---- ⭐⭐ THE OUTBOUND WIDENING, AND WHEN IT IS NOT WORKING -------
              *
              * ⛔ 714 orders were hidden on 2026-09-09 because Deposco puts
              * *picked* and *shipped* at the same `CurrentStatus`. This rule shows
              * them. ⚠️ If the code lookup fails the panel silently goes back to
              * over-hiding — so the (i) has to distinguish "the rule did not
              * apply" from "the rule could not be read".
              */}
            {c && (
                <div className="border-t pt-1">
                    {c.shippingWidened ? (
                        <>⭐ Outbound orders that have <span className="font-medium text-foreground">not left the building</span>{' '}
                        are shown even when the WMS calls them complete — picked and packed still needs a door.{' '}
                        {/* ⚠️ NAMED BECAUSE ITS ABSENCE IS INVISIBLE. A cancelled order never ships, so it
                            qualifies as "not shipped" permanently; the rule excludes it, and a scheduler
                            wondering why a cancelled load is missing from an otherwise-widened list has
                            nowhere else to find that out. */}
                        <span className="text-muted-foreground">Cancelled orders are not included.</span></>
                    ) : !c.shippingCodesRead ? (
                        <>⛔ <span className="font-medium text-foreground">The shipping codes could not be read</span>,
                        so outbound orders the WMS calls complete are hidden — including any that are packed and
                        still waiting for a truck.</>
                    ) : (
                        <>The not-yet-shipped rule is not applied to this list, because open orders are not among
                        the statuses you asked for.</>
                    )}
                </div>
            )}

            {/**
              * ---- ⛔ WHICH WMS HAS BEEN CLASSIFIED AT ALL ---------------------
              *
              * ⚠️ Camelot's statuses are grouped per WMS but have NEVER been
              * measured per DIRECTION — we do not know which belong to receipts
              * and which to shipments. ⛔ An empty section would read as "nothing
              * to hide here"; naming the gap puts it where somebody will see it
              * rather than only in an open-items file.
              */}
            {c && sourcesWithLegend.length > 0 && (
                <div className="border-t pt-1">
                    Classified so far: <span className="font-medium text-foreground">
                        {sourcesWithLegend.join(', ')}
                    </span>.
                    {!sourcesWithLegend.includes('Camelot') && (
                        <> ⛔ <span className="font-medium text-foreground">Camelot is not classified</span>{' '}
                        — its orders are all shown, none hidden.</>
                    )}
                </div>
            )}

            {/**
              * ---- ⭐⭐ CONFIG OR DEPLOY — THE QUESTION PETE ASKED --------------
              *
              * > *"This is a hardcode query or in a config screen?"*
              *
              * ⛔ THE HONEST ANSWER IS "BOTH", AND WITHOUT THIS TABLE A SCHEDULER
              * CANNOT TELL. Somebody asks for a change to what the panel pulls;
              * two of those changes are an UPDATE and three need a release, and
              * the difference is a week.
              */}
            {c && c.provenance.length > 0 && (
                <div className="border-t pt-1">
                    <div className="font-medium text-foreground">Where each rule lives</div>
                    {c.provenance.map(p => (
                        <div key={p.rule}>
                            <span className={p.changeBy === 'UPDATE' ? 'text-foreground' : ''}>
                                {p.changeBy === 'UPDATE' ? '⭐ editable' : '⛔ needs a release'}
                            </span>
                            {' — '}{p.rule} <span className="opacity-70">({p.livesIn})</span>
                        </div>
                    ))}
                </div>
            )}

            {/**
              * ⭐ THE QUERY ITSELF, BEHIND A TOGGLE. Off by default — a scheduler
              * does not read SQL — but one click away for whoever is asked why a
              * load is missing. ⛔ It is the string the server actually ran.
              */}
            {c && c.whereSql && (
                <div className="border-t pt-1">
                    <button type="button" onClick={() => setShowSql(v => !v)}
                            className="underline underline-offset-2 hover:text-foreground">
                        {showSql ? 'Hide the query' : 'Show the query'}
                    </button>
                    {showSql && (
                        <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap rounded border bg-background p-1.5 text-[9px] leading-tight">
{`SELECT ... FROM dbo.OrdersCache oc
 WHERE ${c.whereSql}`}
                        </pre>
                    )}
                    {showSql && (
                        <div className="mt-1">
                            ⚠️ Parameter <em>names</em> are shown; their values are not — the text
                            above is what ran, not something you can paste and execute.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * ---- ⭐⭐ THE IN / OUT PILL — **ONE** DEFINITION (extracted 2026-09-27) --------
 *
 * > Pete: *"We also need outbound or inbound on the detail screen for that order,
 * > just the same icon we use is fine."*
 *
 * ⭐ *"the same icon"* IS THE REQUIREMENT, SO IT IS THE SAME ELEMENT. The row has
 * carried this pill since 2026-09-09 — `IN` on blue, `OUT` on orange, borrowed from
 * `GanttCalendar` so a chip and an order row read alike. Copying eight lines into the
 * dialog would have satisfied the sentence today and drifted by the next palette
 * change, which is precisely what happened to the two Ⓡ buttons and to four copies of
 * `hhmm` — both found earlier today.
 *   ** "THE SAME AS THAT ONE" IS AN INSTRUCTION TO SHARE THE CODE, NOT TO MATCH THE
 *      APPEARANCE. **
 *
 * ⚠️ NULL RENDERS NOTHING. `directionOfOrderType` returns null for a type nobody has
 * classified, and a pill guessing IN or OUT there would be inventing a fact about
 * somebody's freight.
 */
/**
 * ---- ⭐⭐ A TYPE WITH NO DIRECTION NOW SAYS SO (Pete, 2026-09-27) ------------
 *
 * > Pete, on A10848 (an **Adjustment**): *"This was suppose to have the OUT or IN
 * > icon, is that on the list or was it dropped?"* — and, asked what a
 * > no-direction type should show: **"A neutral grey pill with the type."**
 *
 * ⛔⛔ THE OLD BEHAVIOUR WAS CORRECT AND STILL READ AS A BUG. `null` drew nothing
 * rather than guessing, which is right — but a gap where a pill lives is
 * indistinguishable from a pill that failed to render, and Pete reasonably read
 * it as the feature having been dropped.
 *   ** REFUSING TO GUESS AND FAILING TO RENDER LOOK THE SAME. IF THE REFUSAL
 *      MATTERS, IT HAS TO BE VISIBLE. **
 *
 * ⚠️ GREY IS LOAD-BEARING. Blue is inbound and orange is outbound everywhere in
 * this app; a third colour would invite "which way is grey?". Slate is the
 * app's "no answer" colour and carries no direction at all.
 *
 * ⛔ THE ABBREVIATION IS DERIVED, NOT A LOOKUP TABLE. *"We never hardcode values
 * - one of our major arch guidelines."* — and a map would silently print the
 * full string the first time a WMS invents a type. Multi-word types take their
 * initials, single words take three letters, and the FULL type is in the
 * tooltip either way.
 *
 * ⚠️ IT DOES NOT MEAN "UNKNOWN". `Adjustment` is a perfectly well-known type
 * that simply is not a direction. ⭐ And per **Decision 214** (2026-09-22,
 * *"Warn, then let them through"*) an Adjustment CAN be put on an appointment
 * deliberately — so this pill must not read as a refusal either.
 */
function shortTypeLabel(orderType: string): string {
    const words = orderType.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length > 1) return words.map(w => w[0]).join('').toUpperCase().slice(0, 4);
    return words[0].slice(0, 3).toUpperCase();
}

function DirectionPill({ direction, orderType }: {
    direction: 'INBOUND' | 'OUTBOUND' | null | undefined;
    orderType?: string | null;
}) {
    if (!direction) {
        const t = (orderType ?? '').trim();
        /* ⛔ NO TYPE, NO PILL. An empty grey box says less than nothing. */
        if (!t) return null;
        return (
            <span className="shrink-0 rounded border border-slate-300 bg-slate-100 px-1 text-[9px] font-semibold leading-[14px] text-slate-700"
                  title={`${t} - this order type is neither inbound nor outbound`}>
                {shortTypeLabel(t)}
            </span>
        );
    }
    return (
        <span className={`shrink-0 rounded border px-1 text-[9px] font-semibold leading-[14px] ${
            direction === 'INBOUND'
                ? 'border-blue-300 bg-blue-100 text-blue-800'
                : 'border-orange-300 bg-orange-100 text-orange-800'}`}>
            {direction === 'INBOUND' ? 'IN' : 'OUT'}
        </span>
    );
}

function Fact({ label, value, wrap = false }: { label: string; value: string | null | undefined; wrap?: boolean }) {
    return (
        <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
            {/* ⚠️ AN EM-DASH, NOT A BLANK. A blank cell reads as "not loaded".
                ⭐ `wrap` for a value whose END is the point (Pete, 2026-09-28: "Synced
                from WMS is cut-off") — truncating it hid exactly the "synced 2d ago". */}
            <div className={wrap ? 'break-words' : 'truncate'}>{value && String(value).trim() ? value : '—'}</div>
        </div>
    );
}
